<template>
  <div class="roster-root" :class="{ 'light-mode': !isDarkMode, 'roster-touch': rosterTouchLayout }"
    @pointerdown.capture="recordRosterPointer" @pointermove.capture.passive="recordRosterPointer" @touchstart.capture.passive="recordRosterPointer">
    <!-- Roster Header Bar -->
    <div class="roster-bar">
      <div class="d-flex align-center" style="gap: 12px">
        <div class="roster-bar-title">{{ monthName }}</div>
        <div class="roster-bar-stats">
          <span>{{ employees.length }} MA</span>
          <span class="roster-bar-dot">·</span>
          <span>{{ planData.length }} Schichten</span>
        </div>
        <v-chip v-if="moveSource" color="blue accent-2" text-color="white" close @click:close="cancelMove" x-small class="ml-2">
          <v-icon left x-small>mdi-cursor-move</v-icon>
          Ziel wählen: {{ $moment(moveSource.date).format("DD.MM") }}
        </v-chip>
      </div>

      <div class="d-flex align-center" style="gap: 12px">
        <!-- Legend -->
        <div class="roster-legend">
          <div class="roster-legend-item">
            <div class="roster-ldot" style="border: 2px solid #ffd700"></div>
            <span>Spezial</span>
          </div>
          <div class="roster-legend-item">
            <div class="roster-ldot" style="border: 2px dashed #ff5252"></div>
            <span>Einzel</span>
          </div>
          <div class="roster-legend-item">
            <div class="roster-ldot" style="border: 2px solid #ff5252; box-shadow: 0 0 4px rgba(255, 82, 82, 0.65)"></div>
            <span>Konflikt</span>
          </div>
          <div class="roster-legend-item">
            <div class="roster-ldot" style="background: #e91e63"></div>
            <span>Schicht</span>
          </div>
        </div>
        <!-- Compact Mode Switch -->
        <v-btn x-small depressed :color="isCompact ? 'success' : 'grey darken-3'" class="roster-quality-btn mr-1" @click="toggleCompact">
          <v-icon left x-small>{{ isCompact ? "mdi-view-compact" : "mdi-view-headline" }}</v-icon>
          Kompakt-Modus
        </v-btn>
        <!-- Quality Score -->
        <v-btn x-small depressed :color="qualityScore.color" class="roster-quality-btn" @click="showQualityPanel = !showQualityPanel">
          <v-icon left x-small>mdi-chart-arc</v-icon>
          {{ qualityScore.score }}%
          <v-icon right x-small class="ml-1">{{ showQualityPanel ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
        </v-btn>
      </div>
    </div>

    <div class="roster-tools-bar">
      <span class="roster-tools-hint"><v-icon small class="mr-1">mdi-shape-outline</v-icon>{{ rosterTouchMode ? 'Tag antippen für Details & Aktionen' : 'Tag anklicken → Markierung hinzufügen' }}</span>
      <v-switch v-model="showProvisional" dense hide-details inset class="roster-provisional-switch" color="amber darken-2"
        :dark="isDarkMode" :light="!isDarkMode" label="Provisorische Wünsche" :disabled="markerSaving" />
      <v-progress-circular v-if="rosterToolsLoading" indeterminate size="18" width="2" color="amber darken-2" />
      <span v-else-if="showProvisional && !rosterToolsError" class="roster-tools-hint" aria-live="polite">
        {{ provisionalSummary.days }} Mitarbeitertage offen · {{ provisionalSummary.checks }} mit Schicht zu prüfen
      </span>
    </div>
    <div v-if="rosterTouchLayout" class="roster-touch-hint"><v-icon small>mdi-gesture-swipe-horizontal</v-icon>Seitlich wischen für weitere Tage</div>
    <v-alert v-if="rosterToolsError" dense text type="warning" class="ma-2" role="alert">
      {{ rosterToolsError }}
      <v-btn small text :loading="rosterToolsLoading" @click="loadRosterTools">Erneut laden</v-btn>
    </v-alert>
    <div v-if="showProvisional" class="roster-provisional-legend">
      <span class="roster-pending-key"></span> Gestrichelt: noch nicht bestätigt
      <v-icon x-small color="orange" class="ml-3 mr-1">mdi-alert-outline</v-icon> Mit geplanter Schicht prüfen
    </div>

    <!-- Qualitäts-Score Detail Panel -->
    <v-expand-transition>
      <div v-if="showQualityPanel" class="px-3 pb-3 flex-shrink-0">
        <v-card outlined class="grey darken-4 rounded-lg pa-4">
          <div class="d-flex align-start" style="gap: 24px">
            <!-- Score Gauge -->
            <div class="text-center" style="min-width: 100px">
              <v-progress-circular :value="qualityScore.score" :size="80" :width="8" :color="qualityScore.color" rotate="-90">
                <span class="text-h5 font-weight-bold" :class="qualityScore.color + '--text'">{{ qualityScore.score }}</span>
              </v-progress-circular>
              <div class="text-caption grey--text mt-1">Gesamt</div>
            </div>

            <!-- Kategorien -->
            <div class="flex-grow-1">
              <v-row dense>
                <v-col v-for="cat in qualityScore.categories" :key="cat.name" cols="12" sm="6" md="4" lg="3">
                  <div class="d-flex align-center mb-1">
                    <v-icon x-small :color="cat.color" class="mr-1">{{ cat.icon }}</v-icon>
                    <span class="text-caption font-weight-bold white--text">{{ cat.name }}</span>
                    <v-spacer></v-spacer>
                    <span class="text-caption font-weight-bold" :class="cat.color + '--text'">{{ cat.score }}/{{ cat.maxScore }}</span>
                  </div>
                  <v-progress-linear :value="(cat.score / cat.maxScore) * 100" :color="cat.color" height="4" rounded class="mb-1"></v-progress-linear>
                  <div
                    v-for="(issue, idx) in cat.issues"
                    :key="idx"
                    class="d-flex align-center text-caption"
                    style="font-size: 0.7rem; line-height: 1.3">
                    <v-icon
                      x-small
                      :color="issue.severity === 'error' ? 'red lighten-1' : issue.severity === 'warning' ? 'orange lighten-1' : 'grey lighten-1'"
                      class="mr-1">
                      {{ issue.severity === "error" ? "mdi-alert-circle" : issue.severity === "warning" ? "mdi-alert" : "mdi-information" }}
                    </v-icon>
                    <span class="grey--text text--lighten-1">{{ issue.text }}</span>
                  </div>
                  <div v-if="cat.issues.length === 0" class="text-caption" style="font-size: 0.7rem">
                    <v-icon x-small color="green lighten-1" class="mr-1">mdi-check-circle</v-icon>
                    <span class="green--text text--lighten-1">Perfekt!</span>
                  </div>
                </v-col>
              </v-row>
            </div>
          </div>
        </v-card>
      </div>
    </v-expand-transition>

    <div v-if="loading" class="d-flex justify-center align-center flex-grow-1">
      <div class="text-center">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <p class="mt-4 text-h6 grey--text">Lade Plan...</p>
      </div>
    </div>

    <v-card v-else flat outlined dark class="roster-grid-viewport rounded-0 flex-grow-1 d-flex flex-column grey darken-4"
      style="overflow: hidden" :style="{ '--roster-touch-width': 240 + daysInMonth.length * 44 + 'px' }">
      <div class="roster-grid-surface">
      <div class="roster-grid-header d-flex flex-nowrap grey darken-4 border-bottom" style="z-index: 5">
        <div class="employee-col-header font-weight-bold pa-3 d-flex align-center justify-center grey--text text--lighten-1 text-subtitle-2">
          MITARBEITER / BILANZ
        </div>

        <div class="calendar-grid flex-grow-1 d-flex" :class="{ 'moving-mode': moveSource }">
          <div
            v-for="day in daysInMonth"
            :key="`header-${day.format('YYYY-MM-DD')}`"
            class="day-col text-center py-2 relative-col"
            :class="{
              'weekend-header': day.isoWeekday() >= 6,
              'holiday-header': isPublicHoliday(day),
              'drop-active': moveSource && day.format('YYYY-MM-DD') === moveSource.date,
            }"
            :style="{ width: colWidth + '%' }"
            role="button" :tabindex="moveSource ? -1 : 0"
            :aria-label="day.format('DD.MM.YYYY') + ': Markierung und Wünsche öffnen'"
            @click="openDayTools(null, day.format('YYYY-MM-DD'))"
            @keydown.enter.prevent="openDayTools(null, day.format('YYYY-MM-DD'))"
            @keydown.space.prevent="openDayTools(null, day.format('YYYY-MM-DD'))">
            <div class="text-body-2 font-weight-bold" style="opacity: 0.8">{{ day.format("dd") }}</div>
            <div class="text-h6 font-weight-bold mt-1">{{ day.format("D") }}</div>
            <v-tooltip bottom v-if="isPublicHoliday(day)" :disabled="!rosterTooltipsEnabled" :open-on-focus="false">
              <template v-slot:activator="{ on, attrs }">
                <v-icon x-small color="amber lighten-2" v-bind="attrs" v-on="rosterHoverListeners(on)" class="mt-n1">mdi-star-four-points</v-icon>
              </template>
              <span>{{ getPublicHolidayName(day) }}</span>
            </v-tooltip>
            <v-icon v-if="markerFor(null, day.format('YYYY-MM-DD'))" small class="roster-day-marker"
              :color="markerFor(null, day.format('YYYY-MM-DD')).color"
              :title="markerFor(null, day.format('YYYY-MM-DD')).comment || 'Tagesmarkierung'">
              {{ markerIcon(markerFor(null, day.format('YYYY-MM-DD')).shape) }}
            </v-icon>
          </div>
        </div>
      </div>

      <div style="overflow-y: auto; flex-grow: 1; min-height: 0" class="roster-grid-body custom-scrollbar">
        <div class="d-flex flex-nowrap blue-grey darken-4 border-bottom" v-if="unassignedStats.totalCount > 0 || showUnassignedRow">
          <div class="employee-col-header pa-3 d-flex align-center warning--text">
            <v-icon color="warning" class="mr-2">mdi-alert-circle-outline</v-icon>
            <span class="text-subtitle-2 font-weight-bold">OFFEN ({{ unassignedStats.totalCount }})</span>
          </div>
          <div class="calendar-grid flex-grow-1 d-flex">
            <div
              v-for="day in daysInMonth"
              :key="`unassigned-${day.format('YYYY-MM-DD')}`"
              class="day-col d-flex justify-center align-center py-2"
              :class="{ 'weekend-bg': day.isoWeekday() >= 6 }"
              :style="{ width: colWidth + '%' }">
              <v-menu v-if="getUnassignedShiftsForDay(day).length > 0" offset-y :close-on-content-click="false" max-width="400"
                :value="activeRosterMenu === 'unassigned-' + day.format('YYYY-MM-DD')"
                @input="setRosterMenu('unassigned-' + day.format('YYYY-MM-DD'), $event)">
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on" class="unassigned-dot elevation-2 pulse-hover">
                    {{ getUnassignedShiftsForDay(day).length }}
                  </div>
                </template>

                <v-card class="grey darken-3 dark" :class="{ 'roster-touch-actions': rosterTouchMode }">
                  <v-card-title class="subtitle-2 white--text py-2 border-bottom"> Offene Schichten ({{ day.format("DD.MM") }}) </v-card-title>
                  <v-list dense dark class="grey darken-3">
                    <v-list-item v-for="shift in getUnassignedShiftsForDay(day)" :key="shift._id" class="px-2 border-bottom-dashed">
                      <v-list-item-content>
                        <v-list-item-title class="yellow--text font-weight-bold">
                          {{ shift.shiftTemplate ? shift.shiftTemplate.name : "Custom" }}
                        </v-list-item-title>
                        <v-list-item-subtitle class="grey--text text--lighten-2">
                          {{ formatShiftTime(shift) }}
                        </v-list-item-subtitle>
                        <div class="text-caption red--text text--lighten-3 mt-1" v-if="shift.allocationNote">
                          {{ shift.allocationNote }}
                        </div>
                      </v-list-item-content>
                      <v-list-item-action class="d-flex flex-row align-center" style="gap: 8px">
                        <v-btn small color="success" depressed @click="openAssignDialog(shift)"> Zuweisen </v-btn>
                        <v-btn icon small color="red lighten-1" @click="deleteShift(shift)" title="Schicht löschen">
                          <v-icon small>mdi-close</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-menu>
            </div>
          </div>
        </div>

        <v-expansion-panels
          accordion
          flat
          hover
          v-if="processedEmployees.length > 0"
          multiple
          v-model="openPanels"
          :class="{ 'compact-mode-panels': isCompact }">
          <v-expansion-panel v-for="employee in processedEmployees" :key="employee._id" class="employee-panel border-bottom"
            :class="{ 'roster-annotated-row': rosterAnnotatedEmployees.has(String(employee._id)) }">
            <v-expansion-panel-header class="pa-0" hide-actions>
              <div class="d-flex flex-nowrap w-100 fill-height">
                <div
                  :class="[
                    'employee-col-info border-right d-flex grey darken-3',
                    isCompact ? 'pa-2 align-center justify-space-between' : 'pa-3 flex-column justify-center',
                  ]"
                  :style="isCompact ? { height: rosterTouchLayout || rosterAnnotatedEmployees.has(String(employee._id)) ? '60px' : '40px' } : {}">
                  <template v-if="isCompact">
                    <div class="text-truncate mr-2" style="max-width: 155px">
                      <div class="text-body-2 font-weight-bold white--text text-truncate" :title="employee.fullName">
                        {{ employee.fullName }}
                      </div>
                      <div class="text-caption grey--text text-truncate mt-n1" style="font-size: 0.7rem !important" v-if="employee.studioTitle">
                        {{ employee.studioTitle }}
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <div class="d-flex align-center mb-2">
                      <v-avatar size="40" class="mr-3 elevation-2">
                        <img :src="employee.picture ? '/img/profilbilder' + employee.picture : '/img/default-avatar.png'" />
                      </v-avatar>
                      <div class="text-truncate" style="max-width: 190px">
                        <div class="text-subtitle-1 font-weight-bold lh-1 white--text">{{ employee.fullName }}</div>
                        <div class="text-caption grey--text text--lighten-1 mt-1">{{ employee.studioTitle || "Team" }}</div>
                      </div>
                    </div>
                  </template>

                  <div class="roster-hours-menu-wrap" :class="{ 'is-compact': isCompact }" @click.stop @mousedown.stop>
                    <v-menu
                      v-if="employee.hoursBreakdown && employee.hoursBreakdown.available"
                      :open-on-hover="!rosterTouchMode"
                      :open-on-click="true"
                      :value="activeRosterMenu === 'hours-' + employee._id"
                      @input="setRosterMenu('hours-' + employee._id, $event)"
                      offset-y
                      :close-on-content-click="false"
                      :dark="isDarkMode"
                      :light="!isDarkMode"
                      min-width="330"
                      max-width="370">
                      <template v-slot:activator="{ on, attrs }">
                        <div
                          class="roster-hours-trigger"
                          :class="{ 'is-compact': isCompact }"
                          role="button"
                          tabindex="0"
                          aria-label="Sollstunden-Aufschlüsselung öffnen"
                          v-bind="attrs"
                          v-on="on">
                          <div>
                            <span class="grey--text text--lighten-1">{{ isCompact ? "S:" : "Gesamt-Soll:" }}</span>
                            <span class="white--text font-weight-bold ml-1">{{ (employee.sollStunden || 0).toFixed(0) }}h</span>
                          </div>
                          <div>
                            <span class="grey--text text--lighten-1">{{ isCompact ? "P:" : "Gesamt-Plan:" }}</span>
                            <span
                              class="ml-1"
                              :class="
                                employee.plannedHours < employee.sollStunden ? 'error--text font-weight-bold' : 'success--text font-weight-bold'
                              ">
                              {{ (employee.plannedHours || 0).toFixed(0) }}h
                            </span>
                          </div>
                          <v-icon x-small class="roster-hours-trigger__icon">mdi-information-outline</v-icon>
                        </div>
                      </template>

                      <v-card class="roster-hours-popover pa-0" :dark="isDarkMode" :light="!isDarkMode">
                        <div class="roster-hours-popover__header">
                          <div class="roster-hours-popover__icon"><v-icon small color="cyan accent-3">mdi-calculator-variant-outline</v-icon></div>
                          <div>
                            <div class="font-weight-bold">So entstehen die Sollstunden</div>
                            <div class="text-caption roster-hours-muted">{{ employee.fullName }} · {{ monthName }}</div>
                          </div>
                        </div>

                        <div class="roster-hours-formula">
                          <div v-if="employee.hoursBreakdown.hasGrossTarget" class="roster-hours-row">
                            <span>Kalender-Soll vor Abzügen</span>
                            <strong>{{ formatRosterHours(employee.hoursBreakdown.grossTargetHours) }}</strong>
                          </div>
                          <div v-if="employee.hoursBreakdown.vacationHours > 0" class="roster-hours-row is-deduction">
                            <span><v-icon x-small>mdi-minus</v-icon> Ferien</span>
                            <strong>−{{ formatRosterHours(employee.hoursBreakdown.vacationHours) }}</strong>
                          </div>
                          <div v-if="employee.hoursBreakdown.absenceHours > 0" class="roster-hours-row is-deduction">
                            <span><v-icon x-small>mdi-minus</v-icon> Abwesenheiten</span>
                            <strong>−{{ formatRosterHours(employee.hoursBreakdown.absenceHours) }}</strong>
                          </div>
                          <div v-if="employee.hoursBreakdown.adjustmentHours !== 0" class="roster-hours-row is-adjustment">
                            <span><v-icon x-small>mdi-swap-vertical</v-icon> Soll-Korrektur</span>
                            <strong>{{ formatSignedRosterHours(employee.hoursBreakdown.adjustmentHours) }}</strong>
                          </div>
                          <div class="roster-hours-row is-net">
                            <span>Globales Netto-Soll</span>
                            <strong>{{ formatRosterHours(employee.hoursBreakdown.globalTargetHours) }}</strong>
                          </div>
                          <div class="roster-hours-basis text-caption roster-hours-muted">
                            {{ employee.hoursBreakdown.targetWorkdays }} Solltage ·
                            {{ formatRosterHours(employee.hoursBreakdown.dailyTargetHours) }} pro Tag ·
                            {{ employee.hoursBreakdown.contractPercentage }}% Pensum
                          </div>
                        </div>

                        <div class="roster-hours-assistant-target">
                          <div>
                            <div class="roster-hours-eyebrow">ASSISTENT · DIESES STUDIO</div>
                            <div class="roster-hours-target-value">{{ formatRosterHours(employee.hoursBreakdown.studioTargetHours) }}</div>
                          </div>
                          <div class="text-right">
                            <div class="text-caption font-weight-bold">Ziel für die Zuweisung</div>
                            <div class="text-caption roster-hours-muted">
                              Netto-Soll − {{ formatRosterHours(employee.hoursBreakdown.externalPlannedHours) }} extern geplant
                            </div>
                          </div>
                        </div>

                        <div class="roster-hours-plan-grid">
                          <div>
                            <span>Dieses Studio</span><strong>{{ formatRosterHours(employee.hoursBreakdown.localPlannedHours) }}</strong>
                          </div>
                          <div>
                            <span>Andere Studios</span><strong>{{ formatRosterHours(employee.hoursBreakdown.externalPlannedHours) }}</strong>
                          </div>
                          <div>
                            <span>Gesamt geplant</span><strong>{{ formatRosterHours(employee.hoursBreakdown.globalPlannedHours) }}</strong>
                          </div>
                        </div>
                        <div class="roster-hours-footnote">
                          <v-icon x-small>mdi-information-outline</v-icon>
                          Feiertage, Ein-/Austritt und ggf. Schultage sind bereits in den Solltagen berücksichtigt.
                        </div>
                      </v-card>
                    </v-menu>

                    <div v-else class="roster-hours-trigger" :class="{ 'is-compact': isCompact }">
                      <div>
                        <span class="grey--text text--lighten-1">{{ isCompact ? "S:" : "Gesamt-Soll:" }}</span>
                        <span class="white--text font-weight-bold ml-1">{{ (employee.sollStunden || 0).toFixed(0) }}h</span>
                      </div>
                      <div>
                        <span class="grey--text text--lighten-1">{{ isCompact ? "P:" : "Gesamt-Plan:" }}</span>
                        <span class="white--text font-weight-bold ml-1">{{ (employee.plannedHours || 0).toFixed(0) }}h</span>
                      </div>
                    </div>
                  </div>

                  <template v-if="!isCompact">
                    <div style="font-size: 0.8rem; line-height: 1.4">
                      <div class="d-flex justify-space-between align-center mt-1 px-1 grey darken-4 rounded pa-1" style="font-size: 0.75rem">
                        <span class="red--text text--lighten-1 font-weight-bold">F: {{ employee.stats.types.morning }}</span>
                        <span class="blue--text text--lighten-1 font-weight-bold">Z: {{ employee.stats.types.intermediate }}</span>
                        <span class="green--text text--lighten-1 font-weight-bold">S: {{ employee.stats.types.late }}</span>
                      </div>
                      <div class="d-flex justify-space-between align-center mt-2 pt-2 border-top-dashed">
                        <v-tooltip bottom :disabled="!rosterTooltipsEnabled" :open-on-focus="false">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              x-small
                              label
                              :color="Math.abs(employee.stats.diffMonth) > 12 ? 'red darken-3' : 'green darken-3'"
                              v-bind="attrs"
                              v-on="rosterHoverListeners(on)">
                              {{ employee.stats.diffMonth > 0 ? "+" : "" }}{{ employee.stats.diffMonth.toFixed(0) }}h
                            </v-chip>
                          </template>
                          <span>{{ employee.stats.statusText }}</span>
                        </v-tooltip>
                        <span class="grey--text text--lighten-2 text-caption"
                          ><strong>{{ employee.stats.weekendCount }}</strong> WE</span
                        >
                      </div>
                      <div v-if="getEmployeePrevContext(employee)" class="d-flex align-center mt-1 pt-1" style="font-size: 0.7rem">
                        <v-tooltip bottom :disabled="!rosterTooltipsEnabled" :open-on-focus="false">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip x-small label color="blue-grey darken-3" v-bind="attrs" v-on="rosterHoverListeners(on)" class="mr-1">
                              <v-icon x-small left>mdi-history</v-icon>
                              {{ getEmployeePrevContext(employee).streak }}d Streak
                            </v-chip>
                          </template>
                          <span>{{ getEmployeePrevContext(employee).streak }} aufeinanderfolgende Arbeitstage am Vormonatsende</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="getEmployeePrevContext(employee).prevWeekends > 0" :disabled="!rosterTooltipsEnabled" :open-on-focus="false">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              x-small
                              label
                              :color="getEmployeePrevContext(employee).prevWeekends >= 2 ? 'red darken-4' : 'orange darken-4'"
                              v-bind="attrs"
                              v-on="rosterHoverListeners(on)">
                              {{ getEmployeePrevContext(employee).prevWeekends }} Vor-WE
                            </v-chip>
                          </template>
                          <span>{{ getEmployeePrevContext(employee).prevWeekends }} Wochenende(n) im Vormonat gearbeitet</span>
                        </v-tooltip>
                      </div>
                    </div>
                  </template>
                </div>

                <div class="calendar-grid flex-grow-1 d-flex" :class="{ 'moving-mode': moveSource }">
                  <div
                    v-for="(status, date) in employee.dailyStatus"
                    :key="date"
                    class="day-col d-flex justify-center align-center border-left-light drop-zone cell-hover-trigger"
                    :class="{
                      'weekend-bg': $moment(date).isoWeekday() >= 6,
                      'holiday-bg': isPublicHolidayByStr(date),
                      'drop-active': moveSource && date === moveSource.date,
                      'roster-cell-pending': provisionalFor(employee, date),
                      'roster-cell-check': provisionalFor(employee, date) && provisionalFor(employee, date).needsCheck,
                    }"
                    :style="{ width: colWidth + '%' }"
                    role="button" tabindex="0" :aria-label="employee.fullName + ', ' + $moment(date).format('DD.MM') + ': Tagesaktionen'"
                    @click.stop="handleCellClick(employee, date)"
                    @keydown.stop
                    @keydown.enter.self.prevent="handleCellClick(employee, date)"
                    @keydown.space.self.prevent="handleCellClick(employee, date)">
                    <button v-if="markerFor(employee, date)" type="button" class="roster-cell-marker"
                      :tabindex="rosterTouchLayout ? -1 : 0"
                      :aria-label="'Markierung: ' + (markerFor(employee, date).comment || 'bearbeiten')"
                      :title="markerFor(employee, date).comment || 'Markierung bearbeiten'" @click.stop="handleCellClick(employee, date)">
                      <v-icon small :color="markerFor(employee, date).color">{{ markerIcon(markerFor(employee, date).shape) }}</v-icon>
                    </button>
                    <button v-if="provisionalFor(employee, date)" type="button" class="roster-cell-pending-icon"
                      :tabindex="rosterTouchLayout ? -1 : 0"
                      :aria-label="provisionalFor(employee, date).requests.length + ' offene Wünsche anzeigen'"
                      :title="provisionalFor(employee, date).needsCheck ? 'Provisorischer Wunsch: mit Schicht prüfen' : 'Provisorischer Wunsch'"
                      @click.stop="handleCellClick(employee, date)">
                      <v-icon x-small :color="provisionalFor(employee, date).needsCheck ? 'orange' : 'amber darken-2'">
                        {{ provisionalFor(employee, date).needsCheck ? 'mdi-alert-outline' : getStatusIcon(provisionalFor(employee, date).requests[0].type) }}
                      </v-icon>
                    </button>
                    <div
                      v-if="status && status.type === 'shift'"
                      style="width: 100%; display: flex; justify-content: center; gap: 4px; flex-wrap: nowrap; padding: 0 2px">
                      <div v-for="shift in status.allShifts" :key="shift._id" style="flex: 1; min-width: 0; display: flex; justify-content: center">
                        <v-menu offset-y :close-on-content-click="true" :disabled="rosterTouchMode || !!moveSource"
                          :value="activeRosterMenu === 'shift-' + shift._id" @input="setRosterMenu('shift-' + shift._id, $event)">
                          <template v-slot:activator="{ on: menu, attrs }">
                            <v-tooltip bottom :disabled="!rosterTooltipsEnabled" :open-on-focus="false">
                              <template v-slot:activator="{ on: tooltip }">
                                <div
                                  v-bind="attrs"
                                  v-on="{ ...rosterHoverListeners(tooltip), ...rosterShiftMenuListeners(menu) }"
                                  class="shift-pill elevation-1 d-flex align-center justify-center"
                                  :class="{
                                    'shift-pill-special': isSpecialShift(shift),
                                    'shift-pill-island': shift.isIsland,
                                    'shift-pill-conflict': hasSchoolConflict(status, shift),
                                    'shift-moving': moveSource && moveSource._id === shift._id,
                                  }"
                                  :style="{ backgroundColor: shift.shiftTemplate ? shift.shiftTemplate.color : '#E91E63' }"
                                  @click.stop="handleShiftClick(shift, employee, date)">
                                  <v-icon x-small color="white" v-if="hasSchoolConflict(status, shift)" class="mr-1">mdi-alert-circle</v-icon>
                                  <v-icon x-small color="red accent-2" v-else-if="shift.isIsland" class="mr-1">mdi-alert-circle</v-icon>
                                  <v-icon x-small color="white" v-if="isSpecialShift(shift)">mdi-star</v-icon>
                                  <span
                                    class="white--text font-weight-bold"
                                    style="font-size: 0.65rem; opacity: 0.95"
                                    v-if="isAdditionalStudioShift(shift)">
                                    {{ getStudioAbbreviation(shift.studio) }}
                                  </span>
                                </div>
                              </template>
                              <div class="text-center">
                                <div class="subtitle-2 font-weight-bold">
                                  {{ shift.shiftTemplate ? shift.shiftTemplate.name : "Spezial" }}
                                </div>
                                <div class="text-caption">{{ formatShiftTime(shift) }}</div>
                                <div class="text-caption font-weight-bold blue-grey--text text--lighten-3" v-if="shift.studio">
                                  Studio: {{ getStudioName(shift.studio) }}
                                </div>
                                <div v-if="shift.allocationNote" class="yellow--text mt-1 text-caption font-weight-bold">
                                  {{ shift.allocationNote }}
                                </div>
                                <div v-if="shift.warning" class="red--text text--lighten-3 font-weight-bold mt-1 text-caption border-top pt-1">
                                  <v-icon x-small color="red lighten-3">mdi-alert</v-icon> {{ shift.warning }}
                                </div>
                                <div
                                  v-if="hasSchoolConflict(status, shift)"
                                  class="red--text text--lighten-3 font-weight-bold mt-1 text-caption border-top pt-1">
                                  <v-icon x-small color="red lighten-3">mdi-school</v-icon>
                                  Konflikt: {{ employee.fullName }} hat an diesem Tag Berufsschule.
                                </div>
                              </div>
                            </v-tooltip>
                          </template>
                          <v-list dense>
                            <v-list-item @click="openDayTools(employee, date)">
                              <v-icon left small>mdi-shape-outline</v-icon><v-list-item-title>Markierung & Wünsche</v-list-item-title>
                            </v-list-item>
                            <v-divider></v-divider>
                            <template v-if="hasSchoolConflict(status, shift)">
                              <div class="school-conflict-menu px-3 py-2">
                                <div class="d-flex align-center red--text text--lighten-1 font-weight-bold text-caption">
                                  <v-icon small color="red lighten-1" class="mr-2">mdi-school</v-icon>
                                  Schicht an einem Schultag
                                </div>
                                <div class="text-caption grey--text mt-1">Es wird nichts automatisch geändert.</div>
                              </div>
                              <v-list-item @click="openAssistantForSchoolConflict(shift)">
                                <v-icon left small color="indigo lighten-2">mdi-auto-fix</v-icon>
                                <v-list-item-title>Im Assistenten prüfen</v-list-item-title>
                              </v-list-item>
                              <v-list-item @click="openReleaseSchoolConflict(shift, employee)">
                                <v-icon left small color="orange">mdi-account-minus</v-icon>
                                <v-list-item-title>Zuweisung freigeben</v-list-item-title>
                              </v-list-item>
                              <v-list-item @click="openEmployeeSettingsForConflict(employee)">
                                <v-icon left small color="blue lighten-2">mdi-account-cog</v-icon>
                                <v-list-item-title>Monatseinstellungen prüfen</v-list-item-title>
                              </v-list-item>
                              <v-list-item @click="keepSchoolConflict">
                                <v-icon left small color="grey lighten-1">mdi-check-circle-outline</v-icon>
                                <v-list-item-title>Schicht bewusst so belassen</v-list-item-title>
                              </v-list-item>
                              <v-divider></v-divider>
                            </template>
                            <v-list-item @click="startMove(shift)">
                              <v-icon left small>mdi-cursor-move</v-icon> <v-list-item-title>Verschieben / Tauschen</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="deleteShift(shift)" class="red--text">
                              <v-icon left small color="red">mdi-delete</v-icon> <v-list-item-title>Löschen</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </div>
                    </div>

                    <v-tooltip bottom v-else-if="status && status.type === 'externalShift'" :disabled="!rosterTooltipsEnabled" :open-on-focus="false">
                      <template v-slot:activator="{ on, attrs }">
                        <div
                          v-bind="attrs"
                          v-on="rosterHoverListeners(on)"
                          class="shift-pill elevation-1 d-flex align-center justify-center"
                          :style="{
                            'background-color': status.data.isVirtualExternal ? '#2E7D32' : '#607D8B',
                            opacity: status.data.isVirtualExternal ? 0.8 : 0.6,
                            cursor: 'default',
                            border: status.data.isVirtualExternal ? '1px dashed #A5D6A7' : 'none',
                          }">
                          <v-icon x-small color="white">
                            {{ status.data.isVirtualExternal ? "mdi-check-circle-outline" : "mdi-home-outline" }}
                          </v-icon>
                        </div>
                      </template>
                      <div class="text-center">
                        <div class="subtitle-2 font-weight-bold" v-if="status.data.isVirtualExternal">
                          Fremdeinsatz bestätigt (Zeiten unbestätigt)
                        </div>
                        <div class="subtitle-2 font-weight-bold" v-else>
                          Anderes Center: {{ status.data.studio ? status.data.studio.name : "Center" }}
                        </div>
                        <div class="text-caption" v-if="!status.data.isVirtualExternal">
                          {{ status.data.shiftTemplate ? status.data.shiftTemplate.name : "Schicht" }}
                          {{ formatShiftTime(status.data) }}
                        </div>
                      </div>
                    </v-tooltip>

                    <v-tooltip bottom v-else-if="status && status.type !== 'shift'" :disabled="!rosterTooltipsEnabled" :open-on-focus="false">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="rosterHoverListeners(on)" :color="getStatusColor(status.type)" :style="status.isPending ? 'opacity: 0.65;' : ''">
                          {{ getStatusIcon(status.type) }}
                        </v-icon>
                      </template>
                      <span>
                        {{ getStatusText(status.type) }}{{ status.isPending ? " (Ausstehend)" : "" }}
                        <span v-if="status.note"><br />Info: {{ status.note }}</span>
                      </span>
                    </v-tooltip>

                    <div v-else class="empty-slot fill-height w-100 d-flex justify-center align-center">
                      <v-btn
                        v-if="!moveSource && !rosterTouchLayout"
                        icon
                        x-small
                        color="grey lighten-1"
                        class="hover-add-btn"
                        @click.stop="openAddShiftDialog(employee, date)">
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>
              </div>
            </v-expansion-panel-header>
          </v-expansion-panel>
        </v-expansion-panels>
        <div class="pa-4 grey darken-4 border-top-dashed mt-4">
          <v-row align="center" justify="center" v-if="availableEmployees.length > 0">
            <v-col cols="12" sm="8" md="6" class="d-flex align-center">
              <v-autocomplete
                v-model="selectedEmployeeToAdd"
                :items="availableEmployees"
                item-text="fullName"
                item-value="_id"
                label="Weiteren Mitarbeiter einblenden..."
                outlined
                dense
                dark
                hide-details
                placeholder="Mitarbeiter suchen"
                class="mr-4">
                <template v-slot:item="{ item }">
                  <v-avatar size="24" class="mr-3">
                    <img :src="item.picture ? '/img/profilbilder' + item.picture : '/img/default-avatar.png'" />
                  </v-avatar>
                  {{ item.fullName }}
                </template>
              </v-autocomplete>
              <v-btn color="primary" depressed :disabled="!selectedEmployeeToAdd" @click="emitAddEmployee">
                <v-icon left>mdi-account-plus</v-icon> Hinzufügen
              </v-btn>
            </v-col>
          </v-row>
          <div v-else class="text-center grey--text text--darken-1 py-3">
            <v-icon small color="grey darken-1" class="mr-2">mdi-check-all</v-icon>
            Alle Mitarbeiter des Centers sind eingeplant oder werden angezeigt.
          </div>
        </div>
      </div>
      </div>
    </v-card>

    <v-dialog v-model="dayToolsDialog" max-width="560" :persistent="markerSaving" :dark="isDarkMode" :light="!isDarkMode">
      <v-card v-if="dayToolsTarget" class="roster-day-tools" :class="{ 'roster-touch-actions': rosterTouchMode }" :dark="isDarkMode" :light="!isDarkMode">
        <v-card-title>{{ $moment(dayToolsTarget.date).format('dddd, DD. MMMM') }}</v-card-title>
        <v-card-subtitle>
          {{ dayToolsTarget.employee ? dayToolsTarget.employee.fullName : 'Ganzer Tag' }} · {{ getStudioName(dayToolsTarget.studio) }}
        </v-card-subtitle>
        <v-card-text>
          <v-alert v-if="isPublicHolidayByStr(dayToolsTarget.date)" dense text color="amber darken-2" icon="mdi-star-four-points">
            {{ getPublicHolidayName($moment(dayToolsTarget.date)) }}
          </v-alert>
          <div v-if="selectedDayStatus && !selectedDayShifts.length" class="roster-day-status mb-4">
            <v-icon :color="getStatusColor(selectedDayStatus.type)">{{ getStatusIcon(selectedDayStatus.type) }}</v-icon>
            <div>
              <strong>{{ getStatusText(selectedDayStatus.type) }}</strong>
              <div v-if="selectedDayStatus.note" class="roster-marker-comment mt-1">{{ selectedDayStatus.note }}</div>
            </div>
          </div>
          <div v-for="shift in selectedDayShifts" :key="shift._id" class="roster-day-shift mb-4">
            <div class="font-weight-bold">{{ shift.isVirtualExternal ? 'Fremdeinsatz bestätigt' : shift.shiftTemplate ? shift.shiftTemplate.name : 'Spezialschicht' }}</div>
            <div class="mt-1">{{ shift.isVirtualExternal ? 'Zeiten unbestätigt' : formatShiftTime(shift) }}</div>
            <div v-if="shift.studio" class="text-caption mt-1">{{ getStudioName(shift.studio) }}</div>
            <div v-if="shift.allocationNote" class="roster-marker-comment mt-2">{{ shift.allocationNote }}</div>
            <div v-if="shift.warning" class="text-caption orange--text mt-2">{{ shift.warning }}</div>
            <template v-if="selectedDayStatus.type === 'shift' && !markerEditing">
              <v-alert v-if="hasSchoolConflict(selectedDayStatus, shift)" dense text type="warning" class="mt-3 mb-2">Schicht an einem Berufsschultag</v-alert>
              <div v-if="hasSchoolConflict(selectedDayStatus, shift)" class="roster-day-actions mt-2">
                <v-btn outlined small :disabled="markerSaving" @click="runDayShiftAction('assistant', shift)">Im Assistenten prüfen</v-btn>
                <v-btn outlined small :disabled="markerSaving" @click="runDayShiftAction('release', shift)">Zuweisung freigeben</v-btn>
                <v-btn text small :disabled="markerSaving" @click="runDayShiftAction('settings', shift)">Monatseinstellungen</v-btn>
                <v-btn text small :disabled="markerSaving" @click="runDayShiftAction('keep', shift)">Bewusst so belassen</v-btn>
              </div>
              <div class="roster-day-actions mt-3">
                <v-btn outlined small :disabled="markerSaving" @click="runDayShiftAction('move', shift)"><v-icon small left>mdi-cursor-move</v-icon>Verschieben / Tauschen</v-btn>
                <v-btn text small color="error" :disabled="markerSaving" @click="runDayShiftAction('delete', shift)"><v-icon small left>mdi-delete-outline</v-icon>Löschen</v-btn>
              </div>
            </template>
          </div>
          <v-progress-linear v-if="rosterToolsLoading" indeterminate class="mb-3" />
          <v-alert v-if="rosterToolsError" dense text type="warning">{{ rosterToolsError }}</v-alert>
          <template v-if="!markerEditing">
            <div v-if="selectedMarker" class="roster-marker-note mb-3">
              <v-icon :color="selectedMarker.color" size="30">{{ markerIcon(selectedMarker.shape) }}</v-icon>
              <span class="roster-marker-comment">{{ selectedMarker.comment || 'Markierung ohne Kommentar' }}</span>
              <v-btn icon small :disabled="markerSaving || rosterToolsLoading || !!rosterToolsError" aria-label="Markierung bearbeiten" @click="editDayMarker"><v-icon small>mdi-pencil</v-icon></v-btn>
              <v-btn icon small color="error" :loading="markerSaving" :disabled="rosterToolsLoading || !!rosterToolsError" aria-label="Markierung entfernen" @click="saveDayMarker(true)"><v-icon small>mdi-delete-outline</v-icon></v-btn>
            </div>
            <v-btn v-else outlined block class="mb-3" :disabled="rosterToolsLoading || !!rosterToolsError" @click="editDayMarker">
              <v-icon left small>mdi-shape-outline</v-icon>Markierung hinzufügen
            </v-btn>
          </template>
          <div v-else class="roster-marker-editor mb-4">
            <div class="text-caption font-weight-bold mb-2">Form</div>
            <div class="roster-shape-options mb-4">
              <v-btn v-for="shape in markerShapes" :key="shape.value" outlined :color="markerDraft.shape === shape.value ? 'primary' : undefined"
                :aria-pressed="String(markerDraft.shape === shape.value)" :disabled="markerSaving" @click="markerDraft.shape = shape.value">
                <v-icon left small>{{ shape.icon }}</v-icon>{{ shape.text }}
              </v-btn>
            </div>
            <div class="text-caption font-weight-bold mb-2">Farbe</div>
            <div class="roster-color-options mb-4">
              <button v-for="color in markerColors" :key="color.value" type="button" class="roster-color-option"
                :style="{ backgroundColor: color.value }" :aria-label="color.text" :title="color.text"
                :aria-pressed="String(markerDraft.color === color.value)" :disabled="markerSaving" @click="markerDraft.color = color.value">
                <v-icon v-if="markerDraft.color === color.value" color="#172033">mdi-check</v-icon>
              </button>
            </div>
            <v-textarea v-model="markerDraft.comment" label="Kurzer Kommentar (optional)" outlined rows="2" auto-grow
              counter="240" maxlength="240" :disabled="markerSaving" />
            <div class="d-flex justify-end" style="gap: 8px">
              <v-btn text :disabled="markerSaving" @click="markerEditing = false">Abbrechen</v-btn>
              <v-btn color="primary" :loading="markerSaving" :disabled="rosterToolsLoading || !!rosterToolsError || markerDraft.comment.length > 240" @click="saveDayMarker()">Speichern</v-btn>
            </div>
          </div>
          <template v-if="showProvisional && !rosterToolsLoading && !rosterToolsError">
            <div class="text-subtitle-2 mb-2 mt-4">Provisorische Wünsche</div>
            <div v-for="request in selectedProvisionalRequests" :key="request.key" class="roster-request-note" :class="{ 'needs-check': request.conflict }">
              <div class="d-flex align-center" style="gap: 8px">
                <v-icon small :color="getStatusColor(request.type)">{{ getStatusIcon(request.type) }}</v-icon>
                <strong>{{ getStatusText(request.type) || 'Abwesenheit' }}</strong>
                <span class="text-caption">Noch offen</span>
              </div>
              <div v-if="!dayToolsTarget.employeeId" class="mt-1 font-weight-medium">{{ provisionalEmployeeName(request.employee) }}</div>
              <div class="text-caption mt-1">{{ provisionalDetail(request) }}</div>
              <div v-if="request.comment" class="roster-marker-comment mt-1">{{ request.comment }}</div>
              <div class="text-caption mt-2">
                <v-icon x-small :color="request.conflict ? 'orange' : undefined">{{ request.conflict ? 'mdi-alert-outline' : 'mdi-information-outline' }}</v-icon>
                {{ request.conflict === 'overlap' ? 'Überschneidung mit geplanter Schicht' : request.conflict ? 'Schicht vorhanden · genaue Zeiten prüfen' : 'Keine geplante Schicht betroffen' }}
              </div>
            </div>
            <div v-if="!selectedProvisionalRequests.length" class="text-caption">Keine offenen Wünsche an diesem Tag.</div>
            <div class="text-caption mt-3">Planungshilfe: Freigaben und Sollstunden bleiben unverändert. Die Besetzung im Team bitte mitprüfen.</div>
          </template>
          <v-btn v-if="dayToolsTarget.employee && !markerEditing" text block class="mt-4" :disabled="markerSaving" @click="addShiftFromDayTools">
            <v-icon small left>mdi-plus</v-icon>Schicht hinzufügen
          </v-btn>
        </v-card-text>
        <v-card-actions><v-spacer /><v-btn text :disabled="markerSaving" @click="dayToolsDialog = false">Schliessen</v-btn></v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="assignDialog" max-width="500" :dark="isDarkMode" :light="!isDarkMode">
      <v-card :class="isDarkMode ? 'grey darken-3' : 'white'">
        <v-card-title :class="isDarkMode ? 'white--text' : 'black--text'">Mitarbeiter zuweisen</v-card-title>
        <v-card-text class="pt-4">
          <div
            v-if="selectedUnassignedShift"
            class="mb-4 pa-3 rounded border-subtle"
            :class="isDarkMode ? 'grey darken-4 white--text' : 'grey lighten-4 black--text'">
            <strong>Schicht:</strong> {{ selectedUnassignedShift.shiftTemplate ? selectedUnassignedShift.shiftTemplate.name : "Custom" }}<br />
            <strong>Zeit:</strong> {{ formatShiftTime(selectedUnassignedShift) }}
          </div>
          <v-autocomplete
            v-model="selectedEmployeeId"
            :items="employees"
            item-text="fullName"
            item-value="_id"
            label="Mitarbeiter wählen"
            outlined
            dense
            :dark="isDarkMode"
            :light="!isDarkMode">
            <template v-slot:item="{ item }">
              <v-avatar size="24" class="mr-2"><img :src="item.picture ? '/img/profilbilder' + item.picture : '/img/default-avatar.png'" /></v-avatar>
              {{ item.fullName }}
            </template>
          </v-autocomplete>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="grey" @click="assignDialog = false">Abbrechen</v-btn>
          <v-btn color="primary" @click="saveAssignment" :disabled="!selectedEmployeeId">Speichern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="swapDialog" max-width="450" :dark="isDarkMode" :light="!isDarkMode">
      <v-card :class="isDarkMode ? 'grey darken-3' : 'white'">
        <v-card-title :class="isDarkMode ? 'white--text' : 'black--text'">Schichten tauschen?</v-card-title>
        <v-card-text class="pt-2" :class="isDarkMode ? 'white--text' : 'black--text'" v-if="swapSource && swapTarget">
          <div class="d-flex align-center justify-space-between px-2 py-4 rounded mb-2" :class="isDarkMode ? 'grey darken-4' : 'grey lighten-4'">
            <div class="text-center">
              <div class="font-weight-bold text-h6">{{ swapSource.assignedEmployee.fullName }}</div>
              <div class="caption grey--text">von</div>
            </div>
            <v-icon color="blue accent-2">mdi-swap-horizontal</v-icon>
            <div class="text-center">
              <div class="font-weight-bold text-h6">{{ swapEmployee ? swapEmployee.fullName : "..." }}</div>
              <div class="caption grey--text">zu</div>
            </div>
          </div>
          <div class="text-center grey--text text--lighten-1">Datum: {{ $moment(swapSource.date).format("dddd, DD.MM.YYYY") }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="grey" @click="swapDialog = false">Abbrechen</v-btn>
          <v-btn color="blue accent-2" depressed @click="executeSwap">Ja, Tauschen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="addShiftDialog" max-width="600" :dark="isDarkMode" :light="!isDarkMode">
      <v-card :class="isDarkMode ? 'grey darken-3' : 'white'">
        <v-card-title :class="isDarkMode ? 'white--text' : 'black--text'">Neue Schicht hinzufügen</v-card-title>
        <v-card-subtitle class="mt-1 grey--text text--lighten-1">für {{ $moment(newShiftData.date).format("dddd, DD.MM.YYYY") }}</v-card-subtitle>
        <v-card-text class="pt-4">
          <div v-if="availableShifts.length === 0" class="text-center py-2">
            <v-progress-circular indeterminate small color="blue"></v-progress-circular> Lade Vorlagen...
          </div>

          <v-select
            :items="availableShifts"
            v-model="newShiftData.templateId"
            label="Vorlage laden (Optional)"
            item-text="name"
            item-value="_id"
            outlined
            dense
            dark
            clearable
            @change="onTemplateChange"
            class="mb-4">
            <template v-slot:item="{ item }"> <v-chip x-small :color="item.color" class="mr-2"></v-chip> {{ item.name }} </template>
          </v-select>

          <div class="mb-4">
            <div class="d-flex justify-space-between align-center mb-2">
              <span class="text-subtitle-2 white--text">Zeiten & Pausen</span>
              <v-btn x-small color="blue" text @click="addTimeBlock"> <v-icon left x-small>mdi-plus</v-icon> Zeit </v-btn>
            </div>

            <div v-for="(time, i) in newShiftData.times" :key="i" class="d-flex align-center mb-2">
              <v-select
                v-model="time.type"
                :items="['Arbeitszeit', 'Pause']"
                dense
                outlined
                dark
                hide-details
                style="max-width: 130px"
                class="mr-2"></v-select>
              <v-text-field v-model="time.start" type="time" dense outlined dark hide-details class="mr-1 centered-input"></v-text-field>
              <span class="grey--text mx-2">-</span>
              <v-text-field v-model="time.end" type="time" dense outlined dark hide-details class="mr-2 centered-input"></v-text-field>
              <v-btn icon color="red lighten-2" small @click="removeTimeBlock(i)" :disabled="newShiftData.times.length === 1">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>
          </div>

          <v-textarea
            v-model="newShiftData.reason"
            label="Grund für manuelle Schicht (Optional)"
            outlined
            dense
            dark
            rows="2"
            placeholder="z.B. Krankheitsvertretung, Extra-Event..."></v-textarea>

          <v-autocomplete
            v-model="newShiftData.employeeId"
            :items="employees"
            item-text="fullName"
            item-value="_id"
            label="Mitarbeiter direkt zuweisen"
            outlined
            dense
            dark
            clearable>
            <template v-slot:item="{ item }">
              <v-avatar size="24" class="mr-2"><img :src="item.picture ? '/img/profilbilder' + item.picture : '/img/default-avatar.png'" /></v-avatar>
              {{ item.fullName }}
            </template>
          </v-autocomplete>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="grey" @click="addShiftDialog = false">Abbrechen</v-btn>
          <v-btn color="primary" @click="saveNewShift" :disabled="newShiftData.times.length === 0">Erstellen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="releaseConflictDialog" max-width="520" :dark="isDarkMode" :light="!isDarkMode" persistent>
      <v-card class="rounded-xl" :class="isDarkMode ? 'grey darken-3' : 'white'">
        <v-card-title class="d-flex align-center">
          <v-icon color="orange" class="mr-3">mdi-account-minus</v-icon>
          <span class="text-subtitle-1 font-weight-bold">Zuweisung wegen Schultag freigeben?</span>
        </v-card-title>
        <v-card-text v-if="conflictShiftToRelease" class="text-body-2">
          Die Schicht am <strong>{{ $moment(conflictShiftToRelease.date).format("dddd, DD.MM.YYYY") }}</strong> ({{
            formatShiftTime(conflictShiftToRelease)
          }}) bleibt bestehen, wird aber von
          <strong>{{ conflictEmployeeToRelease ? conflictEmployeeToRelease.fullName : "der Person" }}</strong> gelöst.
          <v-alert dense text type="info" class="mt-4 mb-0">
            Danach öffnet sich der Assistent direkt bei dieser Schicht. Es wird niemand automatisch neu eingetragen.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeReleaseSchoolConflict" :disabled="releasingConflict">Abbrechen</v-btn>
          <v-btn color="orange" depressed class="black--text font-weight-bold" @click="releaseSchoolConflict" :loading="releasingConflict">
            Freigeben & Assistent öffnen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import { isPlanningSchoolDay } from "./schoolDayPlanning"
import rosterPlanningTools from "./rosterPlanningTools"

export default {
  name: "ShiftManagementRosterView",
  mixins: [rosterPlanningTools],
  props: {
    availableEmployees: { type: Array, default: () => [] },
    availableStudios: { type: Array, default: () => [] },
    planData: { type: Array, required: true },
    employees: { type: Array, required: true },
    loading: { type: Boolean, default: false },
    startDate: { type: String, required: true },
    schoolHolidays: { type: Array, default: () => [] },
    studioId: { type: String, default: null },
    availableShifts: { type: Array, default: () => [] },
    unassignedShifts: { type: Array, default: () => [] },
    externalShifts: { type: Array, default: () => [] },
    publicHolidays: { type: Array, default: () => [] },
    previousContext: { type: Object, default: null },
    isDarkMode: { type: Boolean, default: true },
  },
  data() {
    return {
      openPanels: [0, 1, 2, 3, 4],
      showQualityPanel: false,
      selectedEmployeeToAdd: null,
      moveSource: null,
      isCompact: localStorage.getItem("rosterIsCompact") === "true",

      // Swap Dialog Data
      swapDialog: false,
      swapSource: null,
      swapTarget: null,
      swapEmployee: null, // Der Mitarbeiter, mit dem getauscht wird

      assignDialog: false,
      selectedUnassignedShift: null,
      selectedEmployeeId: null,

      addShiftDialog: false,
      releaseConflictDialog: false,
      conflictShiftToRelease: null,
      conflictEmployeeToRelease: null,
      releasingConflict: false,
      newShiftData: {
        date: null,
        templateId: null,
        times: [{ start: "08:00", end: "17:00", type: "Arbeitszeit" }],
        reason: "",
        employeeId: null,
      },
    }
  },

  computed: {
    monthName() {
      return this.startDate ? this.$moment(this.startDate).format("MMMM YYYY") : ""
    },
    daysInMonth() {
      if (!this.startDate) return []
      const start = this.$moment(this.startDate).startOf("month")
      const end = this.$moment(this.startDate).endOf("month")
      const days = []
      let day = start.clone()
      while (day.isSameOrBefore(end, "day")) {
        days.push(day.clone())
        day.add(1, "day")
      }
      return days
    },
    colWidth() {
      return this.daysInMonth.length > 0 ? 100 / this.daysInMonth.length : 3
    },
    unassignedStats() {
      const totalCount = this.unassignedShifts.length
      return { totalCount }
    },
    showUnassignedRow() {
      return this.unassignedShifts.length > 0
    },

    // *** PLANUNGS-QUALITÄTS-SCORE ***
    qualityScore() {
      const emps = this.processedEmployees || []
      const totalShifts = (this.planData || []).length
      const unassigned = (this.unassignedShifts || []).length
      if (emps.length === 0 || totalShifts === 0) {
        return { score: 0, color: "grey", categories: [] }
      }

      const categories = []

      // === 2. STUNDEN-BALANCE (25 Punkte) ===
      const balanceIssues = []
      let balanceScore = 25
      const diffs = emps.map((e) => ({ name: e.fullName, diff: e.stats.diffMonth, soll: e.sollStunden || 0 }))
      const overWorked = diffs.filter((d) => d.diff > 8)
      const underWorked = diffs.filter((d) => d.diff < -8)

      overWorked.forEach((d) => {
        balanceScore -= Math.min(5, Math.ceil(Math.abs(d.diff) / 4))
        balanceIssues.push({ severity: d.diff > 16 ? "error" : "warning", text: `${d.name}: +${d.diff.toFixed(0)}h Überstunden` })
      })
      underWorked.forEach((d) => {
        balanceScore -= Math.min(5, Math.ceil(Math.abs(d.diff) / 4))
        balanceIssues.push({ severity: d.diff < -16 ? "error" : "warning", text: `${d.name}: ${d.diff.toFixed(0)}h Unterstunden` })
      })
      balanceScore = Math.max(0, balanceScore)
      categories.push({
        name: "Stunden-Balance",
        icon: "mdi-scale-balance",
        score: balanceScore,
        maxScore: 25,
        color: balanceScore >= 20 ? "green" : balanceScore >= 12 ? "orange" : "red",
        issues: balanceIssues,
      })

      // === 3. WOCHENEND-FAIRNESS (20 Punkte) ===
      const weIssues = []
      let weScore = 20
      const weCounts = emps.map((e) => ({ name: e.fullName, count: e.stats.weekendCount }))
      const maxWE = Math.max(...weCounts.map((w) => w.count), 0)
      const minWE = Math.min(...weCounts.map((w) => w.count), 0)
      const weSpread = maxWE - minWE

      if (weSpread > 2) {
        weScore -= Math.min(10, (weSpread - 2) * 3)
        const topWE = weCounts.filter((w) => w.count === maxWE)
        const bottomWE = weCounts.filter((w) => w.count === minWE)
        topWE.forEach((w) => weIssues.push({ severity: "warning", text: `${w.name}: ${w.count} WE (Maximum)` }))
        bottomWE.forEach((w) => weIssues.push({ severity: "info", text: `${w.name}: ${w.count} WE (Minimum)` }))
      }
      weCounts
        .filter((w) => w.count >= 4)
        .forEach((w) => {
          weScore -= 3
          weIssues.push({ severity: "error", text: `${w.name}: ${w.count} WE (sehr viel!)` })
        })
      weScore = Math.max(0, weScore)
      categories.push({
        name: "WE-Fairness",
        icon: "mdi-calendar-weekend",
        score: weScore,
        maxScore: 20,
        color: weScore >= 16 ? "green" : weScore >= 10 ? "orange" : "red",
        issues: weIssues,
      })

      // === 4. SCHICHTBLÖCKE (15 Punkte) ===
      const blockIssues = []
      let blockScore = 15
      let totalIslands = 0

      emps.forEach((e) => {
        const islands = Object.values(e.dailyStatus || {}).filter((s) => s && s.type === "shift" && s.data && s.data.isIsland)
        if (islands.length > 0) {
          totalIslands += islands.length
          blockIssues.push({
            severity: islands.length >= 3 ? "error" : "warning",
            text: `${e.fullName}: ${islands.length} Einzelschicht${islands.length > 1 ? "en" : ""}`,
          })
        }
      })
      blockScore -= Math.min(15, totalIslands * 3)
      blockScore = Math.max(0, blockScore)
      categories.push({
        name: "Blockbildung",
        icon: "mdi-view-sequential",
        score: blockScore,
        maxScore: 15,
        color: blockScore >= 12 ? "green" : blockScore >= 7 ? "orange" : "red",
        issues: blockIssues,
      })

      // === 5. RHYTHMUS-QUALITÄT (15 Punkte) ===
      const rhythmIssues = []
      let rhythmScore = 15
      let badTransitions = 0

      // Hilfsfunktion: Schichttyp basierend auf Startzeit
      const getShiftCategory = (times) => {
        if (!times || times.length === 0) return null
        const startStr = times[0].start || "08:00"
        const startHour = parseInt(startStr.split(":")[0], 10)
        const startMin = parseInt(startStr.split(":")[1] || "0", 10)
        const startTotal = startHour + startMin / 60
        if (startTotal < 11) return "morning"
        if (startTotal < 14) return "intermediate"
        return "late"
      }

      emps.forEach((e) => {
        const shifts = (e.shifts || []).sort((a, b) => a.date.localeCompare(b.date))
        for (let i = 1; i < shifts.length; i++) {
          const prev = shifts[i - 1]
          const curr = shifts[i]
          // Konsekutive Tage?
          const prevDate = this.$moment(prev.date)
          const currDate = this.$moment(curr.date)
          if (currDate.diff(prevDate, "days") !== 1) continue

          const prevTimes = prev.times || (prev.shiftTemplate ? prev.shiftTemplate.times : [])
          const currTimes = curr.times || (curr.shiftTemplate ? curr.shiftTemplate.times : [])

          const prevType = getShiftCategory(prevTimes)
          const currType = getShiftCategory(currTimes)

          // Gleicher Schichttyp = kein Problem (auch bei 30min Abweichung)
          if (!prevType || !currType || prevType === currType) continue

          // Nur echte Spät→Früh Wechsel sind problematisch
          if (prevType === "late" && currType === "morning") {
            badTransitions++
            rhythmIssues.push({ severity: "warning", text: `${e.fullName}: Spät→Früh am ${this.$moment(curr.date).format("DD.MM")}` })
          }
          // Intermediate→Morning ist weniger schlimm, aber erwähnenswert bei häufigem Vorkommen
          else if (prevType === "late" && currType === "intermediate") {
            // Nur als Info, kein Punktabzug
            rhythmIssues.push({ severity: "info", text: `${e.fullName}: Spät→Zwischen am ${this.$moment(curr.date).format("DD.MM")}` })
          }
        }
      })
      rhythmScore -= Math.min(15, badTransitions * 4)
      rhythmScore = Math.max(0, rhythmScore)
      categories.push({
        name: "Rhythmus",
        icon: "mdi-sleep",
        score: rhythmScore,
        maxScore: 15,
        color: rhythmScore >= 12 ? "green" : rhythmScore >= 7 ? "orange" : "red",
        issues: rhythmIssues,
      })

      // === GESAMT ===
      const totalScore = categories.reduce((sum, c) => sum + c.score, 0)
      const totalMax = categories.reduce((sum, c) => sum + c.maxScore, 0)
      const percentage = Math.round((totalScore / totalMax) * 100)

      let color = "green"
      if (percentage < 70) color = "red"
      else if (percentage < 85) color = "orange"

      return { score: percentage, color, categories }
    },
    processedEmployees() {
      if (!this.employees || this.employees.length === 0) return []
      const currentPlan = this.planData || []

      return this.employees
        .map((emp) => {
          const empId = String(emp._id)
          const empUserId = emp.user._id || emp.user

          const shifts = currentPlan
            .filter((plan) => {
              if (!plan.assignedEmployee) return false
              const planAssignedId = String(plan.assignedEmployee._id || plan.assignedEmployee)
              return planAssignedId === String(empId) || planAssignedId === String(empUserId)
            })
            .sort((a, b) => new Date(a.date) - new Date(b.date))

          // Block-/Serienlogik muss echte Arbeitstage studioübergreifend sehen.
          // `planData` enthält die aktuell eingeblendeten Studios; `externalShifts`
          // ergänzt die übrigen Studios. Reine Wochentags-Vorgaben zählen erst,
          // wenn dafür tatsächlich eine (auch virtuelle/bestätigte) Schicht existiert.
          const externalEmployeeShifts = (this.externalShifts || []).filter((shift) => {
            if (!shift.assignedEmployee || shift.status === "unassigned" || shift.deleted === true || shift.isDeleted === true) return false
            const assignedId = String(shift.assignedEmployee._id || shift.assignedEmployee)
            return assignedId === String(empId) || assignedId === String(empUserId)
          })
          const workedDateSet = new Set(
            [...shifts, ...externalEmployeeShifts]
              .filter((shift) => shift && shift.date)
              .map((shift) => this.$moment(shift.date).format("YYYY-MM-DD")),
          )

          const dailyStatus = {}
          const workedWeekends = new Set()
          let types = { morning: 0, intermediate: 0, late: 0 }

          workedDateSet.forEach((dateStr) => {
            const workedDay = this.$moment(dateStr, "YYYY-MM-DD")
            if (workedDay.isBetween(this.daysInMonth[0], this.daysInMonth[this.daysInMonth.length - 1], "day", "[]") && workedDay.isoWeekday() >= 6) {
              workedWeekends.add(workedDay.isoWeek())
            }
          })

          shifts.forEach((shift) => {
            const times = shift.times || (shift.shiftTemplate ? shift.shiftTemplate.times : [])
            if (times.length > 0) {
              const end = times[times.length - 1].end
              const endHour = parseInt(end.split(":")[0], 10)
              if (endHour <= 18) types.morning++
              else if (endHour >= 20) types.late++
              else types.intermediate++
            }

            const currentDay = this.$moment(shift.date)
            let connectedYesterday = workedDateSet.has(currentDay.clone().subtract(1, "day").format("YYYY-MM-DD"))
            let connectedTomorrow = workedDateSet.has(currentDay.clone().add(1, "day").format("YYYY-MM-DD"))

            if (emp.isApprentice) {
              if (this.isActiveSchoolDay(emp, currentDay.clone().subtract(1, "days"))) connectedYesterday = true
              if (this.isActiveSchoolDay(emp, currentDay.clone().add(1, "days"))) connectedTomorrow = true
            }

            // NEU: Vormonats-Kontext berücksichtigen – wenn MA am Ende des Vormonats gearbeitet hat
            if (!connectedYesterday && this.previousContext) {
              const prevCtx = this.getEmployeePrevContext({ _id: empId })
              if (prevCtx && prevCtx.streak > 0) {
                // Check: Ist der aktuelle Tag am Monatsanfang (1. oder 2.)?
                const dayOfMonth = currentDay.date()
                if (dayOfMonth <= prevCtx.streak) {
                  connectedYesterday = true // Streak vom Vormonat zählt als Verbindung
                }
              }
            }

            shift.isIsland = !connectedYesterday && !connectedTomorrow
            if (shift.isIsland) {
              shift.warning = "Einzel-Schicht"
            } else if (shift.warning === "Einzel-Schicht") {
              delete shift.warning
            }
          })

          this.daysInMonth.forEach((day) => {
            const dateStr = day.format("YYYY-MM-DD")
            const dayOfWeekISO = day.isoWeekday()
            let status = null

            const shiftsOnDay = shifts.filter((s) => s.date === dateStr)
            if (shiftsOnDay.length > 0) {
              if (shiftsOnDay.some((s) => s.isVirtualExternal)) {
                status = { type: "externalShift", data: shiftsOnDay[0], allShifts: shiftsOnDay }
              } else {
                // Für eingeblendete Zusatzstudios fehlen hier bewusst deren
                // studio-spezifische Schulferien. Deshalb markieren wir nur
                // Schichten des aktuell geöffneten Studios als Schulkonflikt.
                const schoolConflictShift = this.isActiveSchoolDay(emp, day)
                  ? shiftsOnDay.find((shift) => !this.isAdditionalStudioShift(shift))
                  : null
                status = {
                  type: "shift",
                  data: schoolConflictShift || shiftsOnDay[0],
                  allShifts: shiftsOnDay,
                  schoolConflict: Boolean(schoolConflictShift),
                }
                if (dayOfWeekISO >= 6) workedWeekends.add(day.isoWeek())
              }
            }

            if (!status) {
              const extShift = this.externalShifts.find((s) => {
                const sEmpId = String(s.assignedEmployee?._id || s.assignedEmployee)
                return (sEmpId === String(empId) || sEmpId === String(empUserId)) && s.date === dateStr
              })
              if (extShift) {
                status = { type: "externalShift", data: extShift }
              }
            }

            if (!status) {
              const approvedAbsence = (emp.sickEntries || []).find((abs) =>
                (!abs.status || abs.status === "accepted") && abs.sickDays && abs.sickDays.some((sd) => sd.date === dateStr),
              )
              if (approvedAbsence) {
                status = {
                  type: approvedAbsence.absenceReason || "sick",
                  isPending: approvedAbsence.status !== "accepted",
                  note: approvedAbsence.note,
                }
              } else if (
                emp.vacations &&
                emp.vacations.find(
                  (vac) => vac.dates && vac.dates.length > 0 && day.isBetween(vac.dates[0].date, vac.dates[vac.dates.length - 1].date, null, "[]"),
                )
              ) {
                status = { type: "vacation" }
              } else if ((emp.freeWishes || []).find((w) => this.$moment(w.day).format("YYYY-MM-DD") === dateStr)) {
                status = { type: "freeWish" }
              } else {
                if (this.isActiveSchoolDay(emp, day)) {
                  status = { type: "school" }
                } else if (emp.planningSettings?.recurringAbsence?.includes(dayOfWeekISO)) {
                  status = { type: "recurringAbsence" }
                } else if (emp.planningSettings?.recurringExternalStudio?.includes(dayOfWeekISO)) {
                  status = { type: "recurringExternalStudio" }
                }
              }
            }
            dailyStatus[dateStr] = status
          })

          const planned = emp.plannedHours || 0
          const soll = emp.sollStunden || 0
          const diffMonth = planned - soll
          const hoursBreakdown = this.getEmployeeHoursBreakdown(emp)

          let statusText = "OK"
          if (diffMonth > 12) statusText = "Überstunden Warnung"
          if (diffMonth < -12) statusText = "Unterstunden Warnung"

          return {
            ...emp,
            studioTitle: emp.studio && emp.studio.length > 0 ? emp.studio[0].title : "",
            shifts,
            dailyStatus,
            hoursBreakdown,
            stats: { weekendCount: workedWeekends.size, diffMonth, types, statusText },
          }
        })
        .sort((a, b) => a.fullName.localeCompare(b.fullName))
    },
  },
  methods: {
    getEmployeeHoursBreakdown(employee) {
      const summary = employee && employee.hoursSummary
      const total = summary && summary.total
      const globalTargetHours = Number(total && total.netTargetHours)
      const globalPlannedHours = Number(total && total.plannedHours)
      if (!total || !Number.isFinite(globalTargetHours) || !Number.isFinite(globalPlannedHours)) return { available: false }

      // Exakt dieselbe zentrale Aufteilung wie im Assistenten: Das globale
      // Netto-Soll wird nur um bereits tatsächlich in anderen Studios geplante
      // Stunden reduziert. Wiederkehrende Wochentagsregeln allein ziehen nichts ab.
      const localSummary = summary.byStudio && summary.byStudio[String(this.studioId)]
      const localPlannedValue = Number((localSummary && localSummary.plannedHours) || 0)
      const localPlannedHours = Number.isFinite(localPlannedValue) ? localPlannedValue : 0
      const externalPlannedHours = Math.max(0, globalPlannedHours - localPlannedHours)
      const studioTargetHours = Math.max(0, globalTargetHours - externalPlannedHours)
      const grossTargetHours = Number(total.grossTargetHours)

      return {
        available: true,
        hasGrossTarget: Number.isFinite(grossTargetHours),
        grossTargetHours: Number.isFinite(grossTargetHours) ? grossTargetHours : 0,
        vacationHours: Number(total.vacationHours) || 0,
        absenceHours: Number(total.absenceHours) || 0,
        adjustmentHours: Number(total.adjustmentHours) || 0,
        globalTargetHours,
        globalPlannedHours,
        localPlannedHours,
        externalPlannedHours,
        studioTargetHours,
        targetWorkdays: Number(summary.targetWorkdays) || 0,
        dailyTargetHours: Number(summary.dailyTargetHours) || 0,
        contractPercentage: Number(summary.contractPercentage) || 0,
      }
    },
    formatRosterHours(value) {
      const number = Number(value)
      return Number.isFinite(number) ? `${number.toFixed(1)}h` : "–"
    },
    formatSignedRosterHours(value) {
      const number = Number(value)
      if (!Number.isFinite(number)) return "–"
      return `${number > 0 ? "+" : number < 0 ? "−" : ""}${Math.abs(number).toFixed(1)}h`
    },
    toggleCompact() {
      this.isCompact = !this.isCompact
      localStorage.setItem("rosterIsCompact", String(this.isCompact))
    },
    isActiveSchoolDay(employee, date) {
      if (!employee) return false
      // The creation wizard passes raw employee records; the month view also
      // provides the resolved cancellations for checks across month boundaries.
      const month = this.$moment(date).format("YYYY-MM")
      const schoolDayExceptions =
        employee.schoolDayExceptions || employee.shiftPlanningSettings?.[this.studioId]?.monthlyOverrides?.[month]?.schoolDayExceptions || []
      return isPlanningSchoolDay({ ...employee, schoolDayExceptions }, date, this.schoolHolidays)
    },
    hasSchoolConflict(status, shift) {
      return Boolean(status && status.type === "shift" && status.schoolConflict && shift && !this.isAdditionalStudioShift(shift))
    },
    openAssistantForSchoolConflict(shift) {
      this.$emit("open-assistant", { shiftId: String(shift._id), date: shift.date })
    },
    openEmployeeSettingsForConflict(employee) {
      this.$emit("open-employee-settings", employee)
    },
    keepSchoolConflict() {
      this.$toast.info("Die Schicht bleibt unverändert und weiterhin als Schulkonflikt markiert.")
    },
    openReleaseSchoolConflict(shift, employee) {
      this.conflictShiftToRelease = shift
      this.conflictEmployeeToRelease = employee
      this.releaseConflictDialog = true
    },
    closeReleaseSchoolConflict() {
      if (this.releasingConflict) return
      this.releaseConflictDialog = false
      this.conflictShiftToRelease = null
      this.conflictEmployeeToRelease = null
    },
    async releaseSchoolConflict() {
      if (!this.conflictShiftToRelease) return
      const shift = this.conflictShiftToRelease
      this.releasingConflict = true
      try {
        await api.shiftManagement.updateShift(shift._id, {
          assignedEmployee: null,
          status: "unassigned",
          allocationNote: "Freigegeben: Konflikt mit Berufsschule",
        })
        this.$toast.success("Zuweisung freigegeben. Die Schicht bleibt zur Neubesetzung offen.")
        this.releaseConflictDialog = false
        this.conflictShiftToRelease = null
        this.conflictEmployeeToRelease = null
        this.$emit("open-assistant", { shiftId: String(shift._id), date: shift.date })
      } catch (error) {
        console.error("Fehler beim Freigeben des Schulkonflikts", error)
        this.$toast.error("Die Zuweisung konnte nicht freigegeben werden.")
      } finally {
        this.releasingConflict = false
      }
    },
    isAdditionalStudioShift(shift) {
      if (!shift || !shift.studio) return false
      const sId = typeof shift.studio === "object" ? shift.studio._id : shift.studio
      return String(sId) !== String(this.studioId)
    },
    getStudioAbbreviation(studio) {
      const name = this.getStudioName(studio)
      return name ? name.substring(0, 2).toUpperCase() : ""
    },
    getStudioName(studioIdOrObj) {
      if (!studioIdOrObj) return ""
      if (typeof studioIdOrObj === "object" && studioIdOrObj.name) return studioIdOrObj.name
      if (typeof studioIdOrObj === "object" && studioIdOrObj.title) return studioIdOrObj.title
      const id = typeof studioIdOrObj === "object" ? studioIdOrObj._id : studioIdOrObj
      const found = this.availableStudios.find((s) => String(s._id) === String(id))
      return found ? found.name : ""
    },
    emitAddEmployee() {
      if (this.selectedEmployeeToAdd) {
        this.$emit("add-employee", this.selectedEmployeeToAdd)
        this.selectedEmployeeToAdd = null // Dropdown zurücksetzen
      }
    },
    startMove(shift) {
      this.moveSource = shift
      this.$toast.info("Verschieben aktiv: Klicke auf ein Ziel im gleichen Tag.")
    },
    cancelMove() {
      this.moveSource = null
    },

    async handleCellClick(employee, dateStr) {
      if (!this.moveSource) {
        this.openDayTools(employee, dateStr)
        return
      }

      if (this.moveSource.date !== dateStr) {
        this.$toast.warning("Verschieben nur am gleichen Tag möglich.")
        return
      }

      if (this.isActiveSchoolDay(employee, dateStr)) {
        this.$toast.warning(`${employee.fullName} hat an diesem Tag Berufsschule. Bitte im Assistenten eine verfügbare Person wählen.`)
        return
      }

      const targetShift = employee.dailyStatus[dateStr] && employee.dailyStatus[dateStr].type === "shift" ? employee.dailyStatus[dateStr].data : null

      if (targetShift && targetShift._id === this.moveSource._id) {
        this.cancelMove()
        return
      }

      if (targetShift) {
        const sourceEmployeeId = String(this.moveSource.assignedEmployee?._id || this.moveSource.assignedEmployee || "")
        const sourceEmployee = this.employees.find(
          (entry) => String(entry._id) === sourceEmployeeId || String(entry.user?._id || entry.user || "") === sourceEmployeeId,
        )
        if (sourceEmployee && this.isActiveSchoolDay(sourceEmployee, dateStr)) {
          this.$toast.warning(
            `${sourceEmployee.fullName} hat an diesem Tag Berufsschule. Ein Tausch würde den Konflikt nur auf die andere Schicht verschieben.`,
          )
          return
        }
      }

      const employeeUserId = employee._id

      try {
        if (targetShift) {
          // SWAP: Öffne Dialog statt sofort loszulegen
          this.swapSource = this.moveSource
          this.swapTarget = targetShift
          this.swapEmployee = employee
          this.swapDialog = true
          // MoveSource nicht resetten, damit wir bei Cancel noch im Modus sind?
          // Nein, besser wir bleiben im Modus bis executed.
        } else {
          // MOVE (leer) - direkt machen
          await api.shiftManagement.updateShift(this.moveSource._id, {
            assignedEmployee: employeeUserId,
            allocationNote: `Verschoben zu ${employee.fullName} (Manuell)`,
          })
          this.$toast.success("Schicht verschoben!")
          this.cancelMove()
          this.$emit("refresh")
        }
      } catch (e) {
        this.$toast.error("Fehler beim Verschieben")
        console.error(e)
        this.cancelMove()
      }
    },

    async executeSwap() {
      if (!this.swapSource || !this.swapTarget || !this.swapEmployee) return

      const sourceEmployeeId = String(this.swapSource.assignedEmployee?._id || this.swapSource.assignedEmployee || "")
      const sourceEmployee = this.employees.find(
        (entry) => String(entry._id) === sourceEmployeeId || String(entry.user?._id || entry.user || "") === sourceEmployeeId,
      )
      if (sourceEmployee && this.isActiveSchoolDay(sourceEmployee, this.swapTarget.date)) {
        this.$toast.warning(
          `${sourceEmployee.fullName} hat an diesem Tag Berufsschule. Bitte die Konfliktschicht freigeben oder einer verfügbaren Person zuweisen.`,
        )
        this.swapDialog = false
        this.cancelMove()
        return
      }

      try {
        const employeeUserId = this.swapEmployee._id

        // A -> B
        await api.shiftManagement.updateShift(this.swapSource._id, {
          assignedEmployee: employeeUserId,
          allocationNote: `Getaucht mit ${this.swapTarget.assignedEmployee.fullName} (Manuell)`,
        })

        // B -> A
        const sourceEmpId = this.swapSource.assignedEmployee ? this.swapSource.assignedEmployee._id || this.swapSource.assignedEmployee : null
        await api.shiftManagement.updateShift(this.swapTarget._id, {
          assignedEmployee: sourceEmpId,
          status: sourceEmpId ? "assigned" : "unassigned",
          allocationNote: `Getaucht mit ${this.swapEmployee.fullName} (Manuell)`,
        })

        this.$toast.success("Erfolgreich getauscht")
        this.$emit("refresh")
      } catch (e) {
        console.error(e)
        this.$toast.error("Tausch fehlgeschlagen")
      } finally {
        this.swapDialog = false
        this.cancelMove()
      }
    },

    async deleteShift(shift) {
      if (confirm("Schicht wirklich löschen?")) {
        try {
          await api.shiftManagement.deleteShift(shift._id)
          this.$toast.success("Gelöscht")
          this.$emit("refresh")
        } catch (e) {
          this.$toast.error("Fehler beim Löschen")
        }
      }
    },

    // --- UNASSIGNED LOGIC ---
    openAssignDialog(shift) {
      this.selectedUnassignedShift = shift
      this.selectedEmployeeId = null
      this.assignDialog = true
    },
    async saveAssignment() {
      if (!this.selectedEmployeeId || !this.selectedUnassignedShift) return
      const selectedEmployee = this.employees.find((employee) => String(employee._id) === String(this.selectedEmployeeId))
      if (selectedEmployee && this.isActiveSchoolDay(selectedEmployee, this.selectedUnassignedShift.date)) {
        this.$toast.warning(`${selectedEmployee.fullName} hat an diesem Tag Berufsschule. Bitte die Zuweisung im Assistenten prüfen.`)
        return
      }
      try {
        await api.shiftManagement.updateShift(this.selectedUnassignedShift._id, {
          assignedEmployee: this.selectedEmployeeId,
          status: "assigned",
          allocationNote: "Manuell zugewiesen (Rest)",
        })
        this.$toast.success("Zugewiesen")
        this.assignDialog = false
        this.$emit("refresh")
      } catch (e) {
        this.$toast.error("Fehler")
      }
    },

    // --- ADD SHIFT LOGIC (UPDATED) ---
    // Jetzt mit Parametern für Pre-Fill
    openAddShiftDialog(arg1, arg2) {
      let dateVal = ""
      let empId = null
      let empStudioId = this.studioId

      // Wenn Arg1 ein Employee Object ist (Klick im Grid)
      if (arg1 && arg1.fullName) {
        empId = arg1._id
        dateVal = arg2 // Datum kommt als 2. Arg
        empStudioId = arg1.loadedFromStudioId || this.studioId
      } else {
        // Wenn Arg1 ein Moment-Objekt ist (Klick im Header)
        dateVal = arg1.format("YYYY-MM-DD")
      }

      this.newShiftData = {
        date: dateVal,
        templateId: null,
        times: [{ start: "08:00", end: "17:00", type: "Arbeitszeit" }],
        reason: "",
        employeeId: empId, // Pre-Set
        studioId: empStudioId,
      }
      this.addShiftDialog = true
    },
    onTemplateChange(val) {
      const tpl = this.availableShifts.find((s) => s._id === val)
      if (tpl && tpl.times && tpl.times.length) {
        this.newShiftData.times = JSON.parse(JSON.stringify(tpl.times))
      }
    },
    addTimeBlock() {
      const last = this.newShiftData.times[this.newShiftData.times.length - 1]
      const type = last.type === "Arbeitszeit" ? "Pause" : "Arbeitszeit"
      this.newShiftData.times.push({ start: last.end, end: this.$moment(last.end, "HH:mm").add(1, "hour").format("HH:mm"), type })
    },
    removeTimeBlock(index) {
      this.newShiftData.times.splice(index, 1)
    },
    async saveNewShift() {
      const selectedEmployee = this.employees.find((employee) => String(employee._id) === String(this.newShiftData.employeeId))
      if (selectedEmployee && this.isActiveSchoolDay(selectedEmployee, this.newShiftData.date)) {
        this.$toast.warning(`${selectedEmployee.fullName} hat an diesem Tag Berufsschule. Erstelle die Schicht offen und besetze sie im Assistenten.`)
        return
      }
      try {
        const payload = {
          studio: this.newShiftData.studioId || this.studioId,
          date: this.newShiftData.date,
          times: this.newShiftData.times,
          shiftTemplate: this.newShiftData.templateId,
          assignedEmployee: this.newShiftData.employeeId,
          allocationNote: this.newShiftData.reason ? `Manuell: ${this.newShiftData.reason}` : "Manuell zugewiesen",
          status: this.newShiftData.employeeId ? "assigned" : "unassigned",
        }
        await api.shiftManagement.createManualShift(payload)
        this.$toast.success("Schicht erstellt")
        this.addShiftDialog = false
        this.$emit("refresh")
      } catch (e) {
        console.error(e)
        this.$toast.error("Fehler beim Erstellen")
      }
    },

    // --- HELPERS ---
    handleShiftClick(shift, employee, date) {
      if (this.rosterTouchMode || this.moveSource) return this.handleCellClick(employee, date)
    },
    isSpecialShift(shift) {
      if (!shift.shiftTemplate) return true
      if (shift.isSpecialDemand === true) return true
      if (shift.allocationNote && (shift.allocationNote.includes("Spezial") || shift.allocationNote.includes("Fix"))) return true
      return false
    },
    getUnassignedShiftsForDay(momentDate) {
      const dateStr = momentDate.format("YYYY-MM-DD")
      return this.unassignedShifts.filter((s) => s.date === dateStr)
    },
    formatShiftTime(shift) {
      if (!shift.times || shift.times.length === 0) {
        if (shift.shiftTemplate && shift.shiftTemplate.times) {
          const first = shift.shiftTemplate.times[0]
          const last = shift.shiftTemplate.times[shift.shiftTemplate.times.length - 1]
          return `${first.start} - ${last.end}`
        }
        return "?"
      }
      const first = shift.times[0]
      const last = shift.times[shift.times.length - 1]
      return `${first.start} - ${last.end}`
    },
    getStatusColor(type) {
      const map = {
        vacation: "blue lighten-1",
        freeWish: "pink lighten-2",
        school: "purple lighten-2",
        recurringAbsence: "grey darken-3",
        publicHoliday: "amber darken-2",
        recurringExternalStudio: "cyan darken-2",
        sick: "red lighten-1",
        military: "deep-purple lighten-1",
        pregnancy: "teal lighten-1",
        other: "grey lighten-1",
      }
      return map[type] || "grey"
    },
    getStatusIcon(type) {
      const map = {
        vacation: "mdi-palm-tree",
        freeWish: "mdi-heart",
        school: "mdi-school",
        recurringAbsence: "mdi-close",
        publicHoliday: "mdi-star-four-points",
        recurringExternalStudio: "mdi-office-building",
        sick: "mdi-emoticon-sad",
        military: "mdi-shield",
        pregnancy: "mdi-baby-carriage",
        other: "mdi-alert-circle-outline",
      }
      return map[type] || "mdi-help"
    },
    getStatusText(type) {
      const map = {
        vacation: "Ferien",
        freeWish: "Wunschfrei",
        school: "Berufsschule",
        recurringAbsence: "Sperrtag",
        publicHoliday: "Feiertag",
        recurringExternalStudio: "Anderes Studio",
        sick: "Krank",
        military: "Militär",
        pregnancy: "Schwangerschaft",
        other: "Anderes",
      }
      return map[type] || ""
    },

    // *** NEU: FEIERTAG HELPERS ***
    isPublicHoliday(momentDay) {
      if (!this.publicHolidays || this.publicHolidays.length === 0) return false
      const dateStr = momentDay.format("YYYY-MM-DD")
      return this.publicHolidays.some((h) => h.date === dateStr)
    },
    isPublicHolidayByStr(dateStr) {
      if (!this.publicHolidays || this.publicHolidays.length === 0) return false
      return this.publicHolidays.some((h) => h.date === dateStr)
    },
    getPublicHolidayName(momentDay) {
      if (!this.publicHolidays) return ""
      const dateStr = momentDay.format("YYYY-MM-DD")
      const holiday = this.publicHolidays.find((h) => h.date === dateStr)
      return holiday ? holiday.name : ""
    },

    // *** NEU: VORMONATS-KONTEXT HELPERS ***
    getEmployeePrevContext(employee) {
      if (!this.previousContext) return null
      const empId = String(employee._id)
      const streak = this.previousContext.streakMap ? this.previousContext.streakMap[empId] || 0 : 0
      const weStats = this.previousContext.weekendStats ? this.previousContext.weekendStats[empId] : null
      const prevWeekends = weStats ? weStats.count : 0
      if (streak === 0 && prevWeekends === 0) return null
      return { streak, prevWeekends }
    },
  },
}
</script>

<style scoped>
.roster-grid-surface { display: flex; flex: 1; flex-direction: column; min-height: 0; }
.roster-grid-header { flex-shrink: 0; }
.roster-grid-viewport { min-height: 0; }
.roster-touch-hint { display: flex; align-items: center; gap: 8px; padding: 0 16px 8px; color: #cbd5e1; font-size: 0.75rem; }
.roster-touch-hint .v-icon { color: inherit; }
.roster-touch .roster-grid-viewport { display: block !important; overflow: auto !important; -webkit-overflow-scrolling: touch; }
.roster-touch .roster-grid-surface { display: block; min-width: var(--roster-touch-width); min-height: 100%; }
.roster-touch .roster-grid-header { position: sticky; top: 0; }
.roster-touch .roster-grid-body { overflow: visible !important; }
.roster-touch .employee-col-header, .roster-touch .employee-col-info { position: sticky; left: 0; z-index: 2; width: 240px; min-width: 240px; }
.roster-touch .employee-col-header { background: #212121; }
.roster-touch.light-mode .employee-col-header { background: #f5f5f7; }
.roster-touch .day-col { flex-shrink: 0; min-height: 44px; touch-action: manipulation; }
.roster-touch .roster-cell-marker, .roster-touch .roster-cell-pending-icon { pointer-events: none; }
.roster-touch .shift-pill:hover { transform: none; }
.roster-touch .shift-pill { height: 28px; }
.roster-touch .unassigned-dot { width: 40px; height: 44px; border-radius: 8px; }
.roster-touch .pulse-hover:hover { animation: none; }
.roster-touch .roster-quality-btn { min-height: 44px; }
.roster-touch .roster-hours-trigger { min-height: 44px; }
.roster-touch .compact-mode-panels >>> .v-expansion-panel-header { min-height: 60px !important; height: 60px !important; }
.roster-touch-actions .v-btn { min-height: 44px; min-width: 44px; }
.roster-touch-actions .v-list-item { min-height: 48px; }
.roster-touch-actions .roster-color-option { width: 44px; height: 44px; }
.roster-day-status { display: flex; align-items: flex-start; gap: 12px; }
.roster-day-shift { border: 1px solid rgba(128, 128, 128, 0.3); border-radius: 10px; padding: 14px; }
.roster-day-actions { display: flex; flex-wrap: wrap; gap: 8px; }
.roster-day-actions .v-btn { text-transform: none; letter-spacing: 0; }
.roster-root.light-mode .roster-touch-hint { color: #475569; }
.roster-tools-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 20px;
  padding: 8px 16px;
  flex-shrink: 0;
  color: #cbd5e1;
}
.roster-tools-hint, .roster-provisional-legend { font-size: 0.75rem; }
.roster-tools-hint .v-icon { color: inherit; }
.roster-provisional-switch { margin: 0; padding: 0; }
.roster-provisional-switch >>> .v-label { font-size: 0.8rem; }
.roster-provisional-legend {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  padding: 0 16px 8px;
  color: #cbd5e1;
}
.roster-pending-key { width: 16px; height: 12px; border: 1px dashed #d69e36; margin-right: 6px; border-radius: 3px; }
.day-col[role="button"] { cursor: pointer; }
.day-col[role="button"]:focus-visible { outline: 2px solid #60a5fa; outline-offset: -3px; }
.roster-cell-pending::after {
  content: "";
  position: absolute;
  inset: 3px;
  border: 1px dashed #d69e36;
  border-radius: 4px;
  pointer-events: none;
}
.roster-cell-check { background-image: repeating-linear-gradient(135deg, transparent, transparent 7px, rgba(251, 191, 36, 0.09) 7px, rgba(251, 191, 36, 0.09) 10px); }
.roster-cell-marker, .roster-cell-pending-icon {
  position: absolute;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 19px;
  border-radius: 4px;
  background: #242a35;
}
.roster-cell-marker { top: 0; right: 0; }
.roster-cell-pending-icon { bottom: 0; left: 0; }
.roster-day-marker { position: absolute; top: 0; right: 0; border-radius: 4px; background: #242a35; }
.roster-marker-note { display: flex; align-items: center; gap: 10px; padding: 12px; border: 1px solid rgba(128, 128, 128, 0.3); border-radius: 10px; }
.roster-marker-comment { white-space: pre-wrap; overflow-wrap: anywhere; flex: 1; min-width: 0; }
.roster-shape-options, .roster-color-options { display: flex; gap: 8px; flex-wrap: wrap; }
.roster-shape-options .v-btn { flex: 1 0 40%; text-transform: none; }
.roster-color-option { width: 38px; height: 38px; border-radius: 50%; border: 3px solid transparent; }
.roster-color-option[aria-pressed="true"] { border-color: #172033; outline: 2px solid #94a3b8; outline-offset: 2px; }
.roster-request-note { padding: 12px; margin-bottom: 8px; border: 1px dashed rgba(128, 128, 128, 0.6); border-radius: 10px; }
.roster-request-note.needs-check { border-color: #d69e36; background: rgba(251, 191, 36, 0.06); }
.roster-root.light-mode .roster-tools-bar, .roster-root.light-mode .roster-provisional-legend { color: #475569; }
.roster-bar { flex-wrap: wrap; gap: 8px; }
.compact-mode-panels >>> .roster-annotated-row .v-expansion-panel-header {
  min-height: 60px !important;
  height: 60px !important;
}
.compact-mode-panels >>> .v-expansion-panel-header {
  min-height: 40px !important;
  height: 40px !important;
}
/* GENERAL LAYOUT */
.employee-col-header,
.employee-col-info {
  width: 280px;
  flex-shrink: 0;
  min-width: 280px;
}
.calendar-grid {
  overflow: hidden;
}
.day-col {
  border-right: 1px solid rgba(255, 255, 255, 0.05);
  position: relative;
}
.weekend-header {
  background-color: rgba(255, 255, 255, 0.05);
  color: #ffcc80;
  white-space: nowrap !important;
}
.weekend-bg {
  background-color: rgba(255, 255, 255, 0.03);
}
.holiday-header {
  background-color: rgba(255, 193, 7, 0.15) !important;
  border-bottom: 2px solid rgba(255, 193, 7, 0.4);
}
.holiday-bg {
  background-color: rgba(255, 193, 7, 0.06) !important;
}
.lh-1 {
  line-height: 1.1;
}
.border-bottom {
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}
.border-right {
  border-right: 1px solid rgba(255, 255, 255, 0.12);
}
.border-left-light {
  border-left: 1px solid rgba(255, 255, 255, 0.03);
}
.border-top-dashed {
  border-top: 1px dashed rgba(255, 255, 255, 0.2);
}
.border-bottom-dashed {
  border-bottom: 1px dashed rgba(255, 255, 255, 0.2);
}
.border-subtle {
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* STUNDEN-AUFSCHLÜSSELUNG */
.roster-hours-menu-wrap {
  width: 100%;
  margin-bottom: 4px;
}
.roster-hours-menu-wrap.is-compact {
  width: auto;
  min-width: 0;
  margin: 0;
  flex-shrink: 0;
}
.roster-hours-trigger {
  width: 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 3px 5px;
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 0.76rem;
  line-height: 1.25;
  cursor: help;
  transition: background-color 0.18s ease, border-color 0.18s ease;
}
.roster-hours-trigger:hover,
.roster-hours-trigger:focus-visible {
  background: rgba(0, 188, 212, 0.08);
  border-color: rgba(24, 255, 255, 0.28);
  outline: none;
}
.roster-hours-trigger.is-compact {
  width: auto;
  gap: 7px;
  padding: 2px 3px;
  font-size: 0.72rem;
}
.roster-hours-trigger__icon {
  color: rgba(128, 222, 234, 0.72) !important;
  flex-shrink: 0;
}
.roster-hours-popover {
  overflow: hidden;
  border: 1px solid rgba(128, 222, 234, 0.2) !important;
  border-radius: 14px !important;
  background-image: linear-gradient(145deg, rgba(0, 188, 212, 0.06), transparent 42%);
}
.roster-hours-popover__header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px 11px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.roster-hours-popover__icon {
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 10px;
  background: rgba(0, 188, 212, 0.12);
}
.roster-hours-muted {
  color: rgba(255, 255, 255, 0.5) !important;
}
.roster-hours-formula {
  padding: 10px 16px 8px;
}
.roster-hours-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 25px;
  font-size: 0.78rem;
}
.roster-hours-row span {
  color: rgba(255, 255, 255, 0.68);
}
.roster-hours-row.is-deduction strong {
  color: #ffab91;
}
.roster-hours-row.is-adjustment strong {
  color: #ffd180;
}
.roster-hours-row.is-net {
  margin-top: 5px;
  padding-top: 8px;
  border-top: 1px dashed rgba(255, 255, 255, 0.14);
  font-size: 0.84rem;
}
.roster-hours-row.is-net span,
.roster-hours-row.is-net strong {
  color: #ffffff;
}
.roster-hours-basis {
  margin-top: 5px;
  line-height: 1.35;
}
.roster-hours-assistant-target {
  margin: 3px 12px 11px;
  padding: 11px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid rgba(0, 230, 118, 0.24);
  border-radius: 11px;
  background: linear-gradient(135deg, rgba(0, 230, 118, 0.12), rgba(0, 188, 212, 0.05));
}
.roster-hours-eyebrow {
  color: #69f0ae;
  font-size: 0.58rem;
  font-weight: 800;
  letter-spacing: 0.75px;
}
.roster-hours-target-value {
  color: #ffffff;
  font-size: 1.15rem;
  font-weight: 800;
  line-height: 1.2;
}
.roster-hours-plan-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin: 0 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 9px;
  overflow: hidden;
}
.roster-hours-plan-grid > div {
  min-width: 0;
  padding: 7px 8px;
  display: flex;
  flex-direction: column;
  gap: 1px;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
}
.roster-hours-plan-grid > div:last-child {
  border-right: 0;
}
.roster-hours-plan-grid span {
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.58rem;
  white-space: nowrap;
}
.roster-hours-plan-grid strong {
  font-size: 0.78rem;
}
.roster-hours-footnote {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  padding: 9px 13px 12px;
  color: rgba(255, 255, 255, 0.43);
  font-size: 0.62rem;
  line-height: 1.35;
}
.roster-hours-footnote .v-icon {
  color: rgba(255, 255, 255, 0.38) !important;
}

/* SHIFT PILL & INTERACTION */
.shift-pill {
  width: 90%;
  height: 24px;
  border-radius: 4px;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  transition: all 0.2s;
}
.shift-pill:hover {
  transform: scale(1.05);
  z-index: 2;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.5);
}
.shift-pill-special {
  border: 2px solid #ffd700 !important;
  box-shadow: 0 0 5px rgba(255, 215, 0, 0.5);
}
.shift-pill-island {
  border: 2px dashed #ff5252 !important;
}
.shift-pill-conflict {
  border: 2px solid #ff5252 !important;
  box-shadow: 0 0 0 2px rgba(255, 82, 82, 0.24), 0 0 10px rgba(255, 82, 82, 0.5) !important;
  animation: school-conflict-pulse 1.8s ease-in-out infinite;
}
.school-conflict-menu {
  background: rgba(244, 67, 54, 0.08);
  border-bottom: 1px solid rgba(244, 67, 54, 0.2);
}
@keyframes school-conflict-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 2px rgba(255, 82, 82, 0.18), 0 0 7px rgba(255, 82, 82, 0.34);
  }
  50% {
    box-shadow: 0 0 0 3px rgba(255, 82, 82, 0.35), 0 0 13px rgba(255, 82, 82, 0.62);
  }
}
.shift-moving {
  animation: pulse 1s infinite;
  border: 2px solid #448aff !important;
  z-index: 10;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(68, 138, 255, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(68, 138, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(68, 138, 255, 0);
  }
}

@keyframes pulse-opacity {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
  100% {
    opacity: 1;
  }
}
.pulse-slow {
  animation: pulse-opacity 2s infinite;
}

.drop-zone {
  transition: background-color 0.2s;
  cursor: pointer;
}

/* CONTAINER STATE (PERFORMANCE FIX) */
.moving-mode .day-col {
  opacity: 0.3;
  filter: grayscale(100%);
  pointer-events: none; /* Block interactions on invalid targets */
  transition: opacity 0.2s;
}

/* EXCEPTION: ACTIVE COLUMN */
.moving-mode .day-col.drop-active {
  opacity: 1;
  filter: none;
  pointer-events: auto; /* Allow interaction */
  background-color: rgba(68, 138, 255, 0.15) !important;
  box-shadow: inset 0 0 0 2px #448aff;
  z-index: 5;
}

/* ADD BUTTON ON HOVER (GRID CELLS) */
.hover-add-btn {
  opacity: 0;
  transition: opacity 0.2s;
}
.cell-hover-trigger:hover .hover-add-btn {
  opacity: 1;
}

/* DOTS & BUTTONS */
.unassigned-dot {
  background: #ff9800;
  color: #000;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  font-size: 12px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.pulse-hover:hover {
  animation: pulse 1s infinite;
}

/* ADD BTN HEADER (Legacy) */
.add-shift-btn {
  position: absolute;
  top: 2px;
  right: 2px;
  opacity: 0;
  transition: opacity 0.2s;
}
.day-col:hover .add-shift-btn {
  opacity: 1;
}

.legend-box {
  width: 12px;
  height: 12px;
  border-radius: 2px;
}
.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #1e1e1e;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #424242;
  border-radius: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #616161;
}

.centered-input >>> input {
  text-align: center;
}

/* Roster Root & Bar */
.roster-root {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #1a1a1a;
}
.roster-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.roster-bar-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: white;
}
.roster-bar-stats {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
  gap: 4px;
}
.roster-bar-dot {
  color: rgba(255, 255, 255, 0.15);
}
.roster-legend {
  display: flex;
  align-items: center;
  gap: 10px;
}
.roster-legend-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.35);
}
.roster-ldot {
  width: 8px;
  height: 8px;
  border-radius: 2px;
}
.roster-quality-btn {
  border-radius: 6px !important;
  text-transform: none !important;
  font-weight: 700 !important;
  font-size: 0.7rem !important;
  letter-spacing: 0.3px !important;
}

/* LIGHT MODE STYLES OVERRIDES */
.roster-root.light-mode {
  background: #f5f5f7;
}
.roster-root.light-mode .roster-bar {
  background: rgba(255, 255, 255, 0.8) !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.roster-root.light-mode .roster-bar-title {
  color: #1d1d1f !important;
}
.roster-root.light-mode .roster-bar-stats {
  color: rgba(0, 0, 0, 0.45) !important;
}
.roster-root.light-mode .roster-legend-item {
  color: rgba(0, 0, 0, 0.45) !important;
}
.roster-root.light-mode .border-bottom {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.roster-root.light-mode .border-right {
  border-right: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.roster-root.light-mode .day-col {
  border-right: 1px solid rgba(0, 0, 0, 0.06) !important;
}
.roster-root.light-mode .weekend-header {
  background-color: rgba(0, 0, 0, 0.03) !important;
  color: #fb8c00 !important;
}
.roster-root.light-mode .weekend-bg {
  background-color: rgba(0, 0, 0, 0.01) !important;
}
.roster-root.light-mode .holiday-header {
  background-color: rgba(255, 193, 7, 0.1) !important;
  border-bottom: 2px solid rgba(255, 193, 7, 0.3) !important;
}
.roster-root.light-mode .holiday-bg {
  background-color: rgba(255, 193, 7, 0.04) !important;
}
.roster-root.light-mode .cell-hover-trigger {
  color: #1d1d1f !important;
}
.roster-root.light-mode .white--text {
  color: #1d1d1f !important;
}
.roster-root.light-mode .grey--text {
  color: rgba(0, 0, 0, 0.5) !important;
}
.roster-root.light-mode .grey.darken-4,
.roster-root.light-mode .grey.darken-3,
.roster-root.light-mode .grey.darken-2 {
  background-color: #ffffff !important;
  color: #1d1d1f !important;
}
.roster-root.light-mode .v-list {
  background-color: #ffffff !important;
}
.roster-root.light-mode .v-card {
  background-color: #ffffff !important;
}
.roster-hours-popover.theme--light {
  background-color: #ffffff !important;
  background-image: linear-gradient(145deg, rgba(0, 150, 136, 0.07), transparent 42%);
  border-color: rgba(0, 137, 123, 0.2) !important;
}
.roster-hours-popover.theme--light .roster-hours-popover__header,
.roster-hours-popover.theme--light .roster-hours-row.is-net {
  border-color: rgba(0, 0, 0, 0.1);
}
.roster-hours-popover.theme--light .roster-hours-muted,
.roster-hours-popover.theme--light .roster-hours-row span,
.roster-hours-popover.theme--light .roster-hours-plan-grid span,
.roster-hours-popover.theme--light .roster-hours-footnote {
  color: rgba(0, 0, 0, 0.52) !important;
}
.roster-hours-popover.theme--light .roster-hours-row.is-net span,
.roster-hours-popover.theme--light .roster-hours-row.is-net strong,
.roster-hours-popover.theme--light .roster-hours-target-value {
  color: #17211f;
}
.roster-hours-popover.theme--light .roster-hours-plan-grid,
.roster-hours-popover.theme--light .roster-hours-plan-grid > div {
  border-color: rgba(0, 0, 0, 0.09);
}
.roster-hours-popover.theme--light .roster-hours-footnote .v-icon {
  color: rgba(0, 0, 0, 0.42) !important;
}
.roster-root.light-mode .custom-scrollbar::-webkit-scrollbar-track {
  background: #f5f5f7;
}
.roster-root.light-mode .custom-scrollbar::-webkit-scrollbar-thumb {
  background: #d1d1d6;
}
.roster-root.light-mode .custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #acacad;
}
</style>
